import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Dropdown from 'flarum/common/components/Dropdown';
import Button from 'flarum/common/components/Button';
import Api from '../utils/Api';
import LessonOptions from '../utils/LessonOptions';
// v0.18.35 — 日期选择器 (flatpickr) 挂载。走公用 helper (自带 vendor 等待 / appendTo /
//   宽度兜底 / 「今天」按钮), 不用自己调 window.flatpickr。
import { mountDatePicker } from '../utils/DatePickerHelper';
// v0.17.0 — 体验课 tab 的「经办人」可搜索单选, 复用 v0.16.7 抽出的公用组件
//   (跟 SalesFollowupCreateModal 同一个实例模式)。不重写一遍是因为 v0.16.6
//   在这个组件上修过 5 处细交互回归 (preventDefault / 三条关闭时机 / 向上翻转…),
//   抄第二份 = 下一轮回归要测两遍。
import SearchableUserPicker from '../utils/SearchableUserPicker';
// v0.17.61 — 「没配课表」的文案 + 出口, 与课表日历的空态**同一份**。
//   改前这个弹窗里的两处占位 (timeSlotDropdown / trialTimeSlotDropdown) 是一句没有出口的
//   死字「该校区未配置课表」—— 用户从「想约课」这条路进来会被直接堵死。详见该文件顶部注释。
import { SCHEDULE_LOADING_TEXT, scheduleMissingCompact,
// v0.17.72 — 课表**问失败**时的第四态占位, 与「没配」刻意分开 (理由见 loadTimeSchedule())
scheduleErrorCompact } from '../utils/ScheduleMissingNotice';
// v0.19.20 (批 41 · 段②) — 「分区骨架」与「字段图标」改用**共用入口**
//   (本仓铁律「一个逻辑一个入口, 不许抄两遍」; 改前这两个原语在本文件里各抄了一份)。
//   ⚠️ 共用入口在 `utils/FormPrimitives.js` —— 本文件的 DOM 结构与样式**一字不动**。
import { renderSection, fieldIcon, errReason } from '../utils/FormPrimitives';
// v0.19.22 (批 43) — 「flatpickr 挂载/销毁」两个动作**共用一个入口**
//   (改前本文件与「转学员」modal 各抄了一份逐字相同的实现)。
import { retryDatePickerIfNeeded, destroyDatePicker } from '../utils/DatePickerState';

/**
 * LessonEditorModal — 约课 / 编辑课程 modal (v0.6.7 + v0.6.8)
 *
 * v0.6.7 (辉哥 9/26 20:44 拍板): 新建约课 modal, 8 字段 v6-row
 * v0.6.8 (辉哥 9/27 拍板): 加 mode='edit' 复用同一 modal, 双击已有 lesson 时间格 → 回显 lesson 数据 + PATCH
 *   ⚠️ v0.8.12: 上句的双击入口已移除, edit 模式现在由右键/长按菜单的"修改约课"触发。
 * v0.8.8 (辉哥 10/7 11:44 报障): edit mode 回显 5 字段全空白 —
 *   改前 oninit 用 id-only 空壳造 initialCoach/initialStudent/initialStudentCard, 把 API 已经序列化好的
 *   coach/student/student_card 完整对象丢了; 类型/分类只读 lesson 表两列, 历史 NULL 数据没有 fallback。
 *   改后: 优先用 lesson.coach / lesson.student / lesson.student_card, 类型/分类 lesson 表优先 + 卡 template fallback。
 *   后端不用改 (ListLessonsController L105-147 早就把这些字段返回来了)。
 *
 * 设计依据: /Users/zhaozihui/.minimax/workspace/booking-card-designs-v6/REPORT.md
 *   - 8 字段 + 备注平铺 5 个视行:
 *     Row 1 · 教室 + 教练  (2 列 grid 50%/50%)
 *     Row 2 · 时间         (日期 chip readonly + 开始 hh:mm + 结束 hh:mm + AUTO hint)
 *     ── 1px 灰色细线 ──
 *     Row 3 · 学员 + 学员卡  (2 列 grid)
 *     Row 4 · 课程类型 + 课程分类 (2 列 grid, filtered by 卡, single-select)
 *     ── 1px 灰色细线 ──
 *     Row 5 · 备注          (textarea)
 *
 * 字段联动 (辉哥拍板):
 *   - 时间格空槽 (桌面右键菜单 / 移动长按菜单) → mode='create' (默认), 自动填日期 chip + 开始时间 + 教室
 *   - 时间格已有 lesson (桌面右键菜单 / 移动长按菜单) → mode='edit', 回显 lesson 数据
 *   ⚠️ v0.8.12 (辉哥 10/7 16:22 拍板): 入口只剩菜单, 桌面 dblclick / 移动 2-tap 两条路径已移除。
 *   - 选学员卡 → 触发: 课程类型 dropdown filtered = 卡关联 1 项 (auto 选中) +
 *                     课程分类 dropdown filtered = 卡关联**第一个**分类 (auto 选中) +
 *                     结束时间 = 开始时间 + 卡 minutes (auto 重算, [+AUTO] hint)
 *   - 改开始时间 → 结束时间跟随重算 (但用户已手动改过结束的话不再跟随)
 *   - 改结束时间 → [+AUTO] hint 消失, 锁住 - 换学员卡 → 课程类型/分类 dropdown 清空 →
 *                  设新卡的关联值, 结束时间按新卡 minutes 重算
 *   - 保存 create → POST /api/ziiven/scheduler/lessons
 *   - 保存 edit   → PATCH /api/ziiven/scheduler/lessons/{id}
 *
 * attrs:
 *   - mode: 'create' (默认) | 'edit'
 *   - start: ISO 字符串 (菜单选中槽位后由 CalendarTab.openBooking 传进来)
 *   - end:   可选 ISO 字符串
 *   - room:  教室对象 (从课表 column 传进来)
 *   - lesson: edit mode 时回显数据 (FC event.extendedProps)
 *   - activeSchoolId: 当前全局校区 id
 *   - onClose: 关闭后回调 (CalendarTab 刷新用)
 *   - lockDate: boolean (v0.18.35, 默认 true = 日期锁死; 只认 false 才解锁)
 *   - defaultTab: 'normal' | 'trial' (v0.18.38, 默认 'normal' = 正课; 只认 'trial')
 *
 * SOP 97: <input> 禁止裸 bidi 属性 (Modal 渲染管线 bug)
 * SOP 98: app.translator.trans(key, params) 默认返数组, confirm/alert 必须传 extract=true
 */
/**
 * v0.9.1 (待补 4) — 'YYYY-MM-DD' → 'YYYY年M月D日'
 *
 * ⚠️ 刻意放在**模块级函数**而不是类方法里, 且不返回值给 JSX 直接消费。
 *   原因见 LessonEditorModal.scopeBanner 的注释: 本仓 JSX 编译链的 pragma
 *   会被 terser 压成单字母, JSX 附近的局部变量一旦被压成同一个字母,
 *   `s("div", ...)` 就会去调用业务变量, 直接把 modal 渲染搞崩。
 *   模块级函数不在压缩的作用域里, 天然安全。
 */
function formatScopeDate(dateStr) {
  const parts = String(dateStr).split('-');
  if (parts.length === 3) return parts[0] + '年' + parts[1] + '月' + parts[2] + '日';
  return String(dateStr);
}
export default class LessonEditorModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    // v0.6.8 — mode 参数 (create / edit) + lesson 回显
    this.mode = vnode.attrs.mode || 'create';
    this.lesson = vnode.attrs.lesson || null;

    // v0.6.7 — 默认 form state (8 字段 + 备注)
    //   create: start 默认 now() (用户从"新建约课"按钮打开, 不传 start)
    //           room 默认 null (新按钮不传 room)
    //   edit:   start/end/room 从 lesson.start_at/end_at/room_id 取
    const startISO = vnode.attrs.start || this.lesson && this.lesson.start_at || new Date().toISOString();
    const startDate = new Date(startISO);
    const endDate = vnode.attrs.end ? new Date(vnode.attrs.end) : this.lesson && this.lesson.end_at ? new Date(this.lesson.end_at) : null;
    this.startDate = startDate;
    this.activeSchoolId = vnode.attrs.activeSchoolId || null;
    // ── v0.18.35 (辉哥 10/20 拍板) — 日期是否锁死 ──────────────────────────
    //   true  = 日期固定不可改 (fc 课表入口: 用户点的是课表上具体某一格, 那一格的日期
    //           就是这次约课的日期) ⇒ 渲染**只读 chip**, 与改动前逐字相同。
    //   false = 日期可选 (「约课管理」的「+ 新建预约」: 用户没指定某一天)。
    //
    // ⚠️ 用 `!== false` 而不是 `!!attrs.lockDate`:
    //   attrs.lockDate 缺失 (老调用方 / 将来新增入口忘了传) ⇒ `undefined !== false` = true
    //   ⇒ **默认锁死**。要可编辑必须**显式**传 false —— 跟 BookingModalHelper 那边
    //   「默认 true, 要可编辑必须显式传 false」是同一条口径, 两边都防「忘了传 ⇒ 意外可改」。
    this.lockDate = vnode.attrs.lockDate !== false;
    // v0.18.35 — flatpickr 生命周期状态 (幂等挂载 + 切 tab 换 DOM 后重挂 + 关闭时销毁)。
    //   instance = 当前 flatpickr 实例; el = 它绑在哪个 input 上;
    //   mounting = 有一次挂载在途 (vendor 异步, 期间不能再挂第二个);
    //   wantEl   = 想绑的 input (在途期间节点变了就更新它, 在途那次结束后自动复查);
    //   failedEl  = 挂不上去的节点 (同一个节点不重复试, 避免 5s 等待死循环)。
    this._dp = {
      instance: null,
      el: null,
      mounting: false,
      wantEl: null,
      failedEl: null
    };
    // v0.17.64 — 调用方 (日历右键建课) 已经知道用户点的是**哪一行**, 直接把那个节点 id 递进来。
    //   它比「按 startTime 反查」更准: 反查命中的是 sort_order **第一条** start_time 相同的节点,
    //   两档起点相同 (10:30 / 10:30) 时就会认错档 —— 而这里给的是「用户实际点的那一行」, 不会认错。
    //   传不传都支持: 传了就用它, 没传 (LessonsTab 的「+新建预约」等入口) 退 backfillScheduleNodeIds() 反查。
    this.prefillNodeId = vnode.attrs.scheduleNodeId != null && Number(vnode.attrs.scheduleNodeId) > 0 ? Number(vnode.attrs.scheduleNodeId) : null;
    this.onCloseCb = vnode.attrs.onClose;
    this.saving = false;
    this.error = '';

    // v0.17.75 — 实时碰撞预览 (防抖 300ms 调 check-conflict) 的状态。
    //   ⚠️ 本弹窗在 v0.17.75 之前**从来没有**调过 check-conflict (那条注释「前端在 form 里
    //     防抖 300ms 调用」写于 v0.6.x, 但全仓唯一真正的调用方一直是 CalendarTab.checkConflicts,
    //     日历循环里给每节课发一次, 跟这个表单**毫无关系**)。所以这里是把预览**新建**出来的,
    //     不是「适配已有的」。改动理由见 scheduleLiveCheck() 注释。
    //   `conflict` = 真的会 422 的 (学员卡 / 教室预留 / 课槽占用) ⇒ 红色, 阻断
    //   `warnings` = 降级下来的 coach/room/student ⇒ 黄色, **不影响保存**
    this.liveCheck = {
      conflict: null,
      warnings: [],
      pending: false
    };
    this._liveSig = null; // 上一次发请求时的表单签名 (没变就不重发)
    this._liveTimer = null; // 300ms 防抖定时器
    this._liveSeq = 0; // 竞态守卫: 只有最后一次请求的结果算数

    // Row 2 时间段 Dropdown 的数据源。
    //   v0.17.57 起读**课表** (GET /schools/{id}/schedule 的 nodes), 不再读老 SCD time-schedule
    //   (那个端点 v0.17.55 已随 business_hours 一起下线, 现在必然 404)。
    //   选项 = 课表里的**时间段**清单 (如 '09:00 – 12:00'), 不是按 lesson+break 逐格算出来的
    //   十几段碎时间段 —— 时间段是纯标记, 用户真正要决定的只是「几点开始」。
    //   modal 打开瞬间课表可能未就绪 (loadTimeSchedule 异步), 期间 dropdown 显示 placeholder;
    //   fetch 完 → m.redraw() → dropdown 自动 refresh 选项。
    this.scheduleNodes = null;
    // v0.17.61 — 课表**问出结论了没有**。与 scheduleNodes 分开存, 理由见 loadTimeSchedule() 注释。
    this.scheduleResolved = false;
    // v0.17.72 — 「问失败了」这个**第四种**结论, 与 scheduleResolved 分开存。
    //   改前失败也被记成 scheduleResolved=true + nodes=null, 于是两个 dropdown
    //   都显示「该校区还没有配置课表」—— 后端 403 被前端说成「你没配」, 理由见 loadTimeSchedule()。
    this.scheduleFailed = false;
    this.loadTimeSchedule();

    // 候选下拉数据 (启动期 1 次 fetch, modal 打开瞬间可能为空)
    this.rooms = [];
    this.coaches = [];
    this.students = [];
    this.studentCards = []; // 当前学员名下所有卡 (含 template)

    // v0.6.8 — edit mode 预填教室/教练/学员 (从 lesson)
    let initialRoom = vnode.attrs.room || null;
    let initialCoach = null;
    let initialStudent = null;
    let initialStudentCard = null;
    if (this.mode === 'edit' && this.lesson) {
      const l = this.lesson;
      if (l.room_id && !initialRoom) {
        initialRoom = {
          id: l.room_id,
          name: '',
          color: '',
          school_id: l.school_id
        };
      }
      // v0.8.8 — edit mode 预填改用 API 已返回的完整对象 (辉哥 10/7 11:44 报障: 5 个字段全空白)
      //   改前: 造 id-only 空壳
      //     coach/student = { id, user_id, display_name: '', username: '' }
      //       → 教练/学员下拉的 label = display_name || username
      //         → 两个字段都是空串 → 渲染成一片空白
      //     student_card = { id }
      //       → studentCardDropdown L505 的 label = name || template_name || `#${id}` → 只剩 "#18"
      //   改后: 优先用 ListLessonsController L105-147 已经序列化好的完整对象
      //     (coach/student 含 username + display_name + avatar_url;
      //      student_card 含 name(=template.name) + template 联表(minutes/lesson_type_id/lesson_category_ids))
      //     对象缺失 (理论上不该发生, 老缓存数据可能) 才退回 id-only 占位保底 — 保留, 别删
      initialCoach = this.normalizeUserObject(l.coach, l.coach_id);
      initialStudent = this.normalizeUserObject(l.student, l.student_id);
      initialStudentCard = this.normalizeCardObject(l.student_card, l.student_card_id);
      // v0.6.8b — V 测 FAIL fix: edit mode 触发 loadStudentCards,
      //   之前学员卡 dropdown 显示 placeholder "该学员暂无学员卡" (this.studentCards.length === 0),
      //   因为学员 dropdown 已是 initialStudent 没触发 onStudentChange → loadStudentCards 没跑
    }

    // v0.6.7 — 默认 form state
    //
    // v0.17.0 — 这里改成**两个独立草稿**, `this.form` 变成 getter 指向当前 tab 那份。
    //   动机: 切 tab 不能丢已填内容 (设计稿硬要求)。原来 `this.form` 是唯一一份,
    //   切过去就得共用 —— 于是「正课填了学员, 切到体验课看卡模板」再切回来,
    //   学员被体验课的改动覆盖掉。
    //   ⚠️ 为什么用 getter 而不是 `onTabSwitch` 里手工搬字段: 搬字段必然漏,
    //     而 getter 让全文件**所有** `this.form.xxx` 的读写自动落到当前草稿上,
    //     一处不漏 (computeTimeSlots / roomDropdown / coachDropdown / validate 等
    //     全部零改动复用)。
    //   正课草稿的**内容**与改前逐字一致 —— 只是从 this.form 搬进了 drafts.normal。
    //
    // v0.18.38 (辉哥 10/20 拍板) — 默认 tab 由 attrs 决定:
    //   '销售跟进' tab 的跟进记录里点「预约」进来的, 默认停在**「体验课」** tab
    //   (从一条销售线索出发去约课 = 约第一次上门的体验课, 不是正课)。
    //   其它入口 (「约课管理」的「+ 新建预约」/ fc 课表右键「约课」) 不传这个参数
    //   ⇒ 仍然是**正课**, 行为跟改动前逐字相同。
    //
    // ⚠️ **只认 'trial'**: 别的值 (undefined / 'TRIAL' / '体验课' / null …) 一律归一为 'normal'。
    //   弹窗里只有 'normal' / 'trial' 两个 tab (tabBar L2283-2309), 停在一个不存在的 tab 上
    //   会让两份草稿都不显示 —— 宁可回落正课。
    // ⚠️ **只在 create 模式生效**: edit 模式**不渲染 tab 栏** (tabBar L2284 `if (this.mode === 'edit') return null`),
    //   而且 edit 的回显数据全写在 drafts.normal 里 —— 若此时把 this.tab 设成 'trial',
    //   弹窗会显示一份**空草稿**的体验课表单 (看着能点, 保存必炸)。
    //   ⇒ edit 模式一律 'normal', 跟改动前一致。
    // ⚠️ 默认值仍然是 'normal' (安全默认): 将来新增入口忘了传 ⇒ 落回正课, 不会意外停在体验课。
    this.tab = this.mode === 'create' && vnode.attrs.defaultTab === 'trial' ? 'trial' : 'normal';
    this.drafts = {
      normal: {
        // Row 1
        room: initialRoom,
        coach: initialCoach,
        // Row 2
        date: this.formatDate(startDate),
        dateLabel: this.formatDateLabel(startDate),
        startTime: this.formatHHMM(startDate),
        // v0.17.62 — 用户显式选的课表节点 id。
        //   edit 模式从 lesson 读回 (老课没这列 ⇒ null ⇒ 走 startTime 反推的兼容路径);
        //   新建模式一律 null, 等用户在下拉里选一次。
        scheduleNodeId: this.lesson && this.lesson.schedule_node_id != null ? +this.lesson.schedule_node_id : null,
        // v0.17.57 — endTime = startTime + 卡模板 minutes (见 recalcEndFromCard),
        //   仍然没有任何「用户手动改过」入口, 所以 endTimeAuto 那个状态位依旧不需要。
        //   ⚠️ 历史自定义课 (startTime 不落在任何时间段上) 的 endTime 一律**原样保留**:
        //   本轮不做任何静默吸附, 用户在下拉里手动选一次时间段即可。
        endTime: endDate ? this.formatHHMM(endDate) : '',
        // Row 3
        student: initialStudent,
        studentCard: initialStudentCard,
        // Row 4
        // v0.8.8 — 课程类型/分类补 fallback 链路 (辉哥 10/7 11:44 报障: 这两个字段也空白)
        //   改前: 只读 lesson 表两列; v0.6.42 之前的历史约课这两列是 NULL → 一律 null
        //     → lessonTypeDropdown / lessonCategoryDropdown 直接 render "未配置" + validate() 拦死保存
        //   改后: lesson 表优先, NULL 时回落到该课学员卡 template 的关联值
        //     (类型 = template.lesson_type_id 单值; 分类 = template.lesson_category_ids JSON 数组取第一个)
        //     跟 ListLessonsController L100 注释里写的兼容约定 + LessonsTab v0.6.42 lessonTypeIdOf 同款
        // v0.12.0 — 计费课时 (半节课时, 辉哥 10/9 拍板)
        //   取值只有 0.5 / 1.0 两个。默认 1.0 —— 这是改前的隐含约定
        //   (「一行 = 一节课」硬编码在 CardChargeService 的 `- 1`), 存量 lesson 一律 1.0,
        //   所以默认必须是一节, 否则老课会被静默改口径。
        //   edit mode 回显 lesson.lesson_charge; 字段可能缺失 (v0.12.0 之前的老缓存),
        //   用 `=== 0.5 ? 0.5 : 1` 兜底 —— 任何非法值都退成一节,
        //   宁可显示成一节也不能让半个值混进账里。
        lessonCharge: this.lesson && Number(this.lesson.lesson_charge) === 0.5 ? 0.5 : 1,
        lessonType: this.resolveLessonType(this.lesson),
        lessonCategory: this.resolveLessonCategory(this.lesson),
        // Row 5
        notes: this.lesson && this.lesson.notes ? this.lesson.notes : ''
      },
      // ── 体验课草稿 (v0.17.0) ──────────────────────────────────────
      //   体验课的客户**不是学员** (还没注册成 Flarum 用户), 所以这份草稿里
      //   **没有 student / studentCard 字段** —— 这是业务前提, 不是漏写。
      //   ⚠️ 「体验课 pane 内不得出现『学员』二字」是硬验收项, 所以这里别加。
      //   排课三字段 (教室/教练/时间) 与正课共用同一套 helpers
      //   (roomDropdown / coachDropdown / computeTimeSlots), 不重复实现。
      trial: {
        room: initialRoom,
        // 从菜单槽位带入的教室, 跟正课同一个初值
        coach: initialCoach,
        date: this.formatDate(startDate),
        dateLabel: this.formatDateLabel(startDate),
        startTime: this.formatHHMM(startDate),
        // v0.17.62 — 体验课同样要记节点 id (它是真实的一节课, 一样占一个课槽)。
        //   体验课没有 lesson 可回显, 新建一律 null。
        scheduleNodeId: null,
        endTime: endDate ? this.formatHHMM(endDate) : '',
        // ① 客户信息 (全手填)
        customer_name: '',
        age: '',
        // 空串一路保持空串, 提交时才归一成 null (同 v0.16.0 口径)
        phone: '',
        lesson_category_id: '',
        lead_channel_id: '',
        personal_note: '',
        sales_id: '',
        // 经办人

        // ② 体验卡 (选**卡模板**, 必选)
        template: null,
        // 课程类型固定 builtin-trial 只读; 课程分类由所选模板自动带出第一个
        lessonType: 'builtin-trial',
        lessonCategory: null
      }
    };

    // v0.17.0 — 体验课 tab 专属的加载态 / 候选
    //   cardTemplates = builtin-trial 且 lessons===1 的卡模板 (下拉候选, 方案 B)
    this.categories = [];
    this.channels = [];
    this.trialSaving = false;
    // ── v0.18.86 (P2-4) — 「该客户已有体验跟进」的提示状态 ──────────────────
    //   state: 'idle' (不查/查不动) | 'checking' | 'has' (有体验跟进) | 'unknown' (查失败)
    //   ⚠️ 'unknown' **不渲染任何结论** —— 查失败绝不等于「没有体验跟进」
    //     (同 SalesFollowupCreateModal 的 phoneCheck 三态纪律)。
    this.trialFollowupCheck = {
      state: 'idle',
      followupCount: 0,
      cardCount: 0
    };
    this._trialFollowupSeq = 0;
    this._trialFollowupTimer = null;
    // v0.17.1 — 经办人候选**懒加载**状态 (辉哥 10/12 07:25 拍板)
    //   loaded    = 已经拉过一轮 (含拉空的情况), 之后再切 tab 直接返回, 不重复请求
    //   inFlight  = 正在拉的那一轮的 promise, 用来防并发 (见 ensureTrialPickerLoaded)
    this.trialPickerLoaded = false;
    this.trialPickerInFlight = null;

    // 经办人下拉复用 v0.16.7 抽出来的 SearchableUserPicker (照 SalesFollowupCreateModal 同款)
    this.trialPicker = new SearchableUserPicker({
      host: this,
      getSchoolId: () => this.activeSchoolId,
      getValue: key => this.drafts.trial[key],
      setValue: (key, v) => {
        this.drafts.trial[key] = v;
      },
      fields: {
        sales_id: {
          role: 'coach,sales,manager',
          label: '经办人',
          icon: 'fa-user-tie',
          required: true,
          groupClass: 'ZS-tf-agent'
        }
      }
    });

    // ── v0.17.12 正课 tab 的「教练 / 学员」改用可搜索单选 ────────────────────
    //
    // 独立实例, **不并进 trialPicker**: trialPicker 是 v0.17.0/1 已经 V 测过的懒加载路径,
    // 并进去会让「只约体验课」的户在切 tab 时白拉一轮教练/学员候选。
    //
    // ## 候选源: 复用宿主已有数组, **零新增请求, 且候选集与改动前逐条同源**
    //   教练 ← this.coaches  (/users?role=coach, load() L488)
    //   学员 ← this.students (students-list,    load() L493)
    // ⚠️ 学员**绝不能**改走 /users?role=student —— 生产实测 (望京凯德茂, school 1):
    //     students-list 8 人, /users?role=student&school_id=1 只有 7 人。差的两个是
    //     id=1 Ziven 与 id=2 dsl —— 他们有 school 1 的卡且绑定了 school 1, 但**不在
    //     「学员」组 (group 7) 里**, RoleResolver 判不出 student 就查不到。换过去等于
    //     让辉哥本人和 dsl 从候选里直接消失。
    //
    // ⚠️ key 名必须与 trialPicker 的 sales_id 彻底不同: 两个实例各挂一个 document
    //   mousedown 监听, updateFlips() 靠 data-zs-picker="<key>" 取元素, key 重名会串。
    this.normalPicker = new SearchableUserPicker({
      host: this,
      getSchoolId: () => this.activeSchoolId,
      // ⚠️ 一律读 this.drafts.normal, 不读 this.form: picker 只在正课 pane 里渲染,
      //   但 getValue 可能被 choose() 之外的路径在切 tab 期间问到, 写死草稿更稳。
      getValue: key => {
        const c = this.drafts.normal.coach;
        const s = this.drafts.normal.student;
        if (key === 'normal_coach_id') return c ? String(c.id || c.user_id) : '';
        return s ? String(s.user_id || s.id) : '';
      },
      // 值必须存**对象**: submit payload 与 onStudentChange/loadStudentCards 都依赖完整对象
      setValue: (key, v, item) => {
        if (key === 'normal_coach_id') {
          this.drafts.normal.coach = item || null;
          m.redraw();
          return;
        }
        // ⚠️ 必须走 onStudentChange, 不能直接赋值 —— 联动 (清学员卡 + 清类型/分类 +
        //   loadStudentCards 拉卡) 全在那里面, 直接赋值会把学员卡联动整条打断。
        this.onStudentChange(item || null);
      },
      // edit mode 回显: 当事人不在候选里 (例如卡已删 / 已退校) 时, 靠它把名字显示出来,
      //   并让清除按钮出现。它**只用于回显**, 组件保证不会把它塞进候选列表。
      currentObject: key => {
        if (key === 'normal_coach_id') return this.drafts.normal.coach || null;
        return this.drafts.normal.student || null;
      },
      // maxVisible 提到 200: 老 coachDropdown/studentDropdown 是**平铺全部**候选不截断,
      //   保持一致才算「候选集逐条不变」 (下拉容器自带 max-height + 滚动, 视觉不受影响)。
      maxVisible: 200,
      // v0.17.13 — 免疫「modal 打开时那次程序化 focus」。
      //   这里是**正课 tab** 自己的 modal, 而 ziven-core v0.5.8 全局 override 了
      //   Modal.prototype.onready, 强制聚焦 modal 里的第一个 input。v0.17.12 把教练框
      //   换成 <input type="text"> 后它就成了第一个 input, 于是打开 modal 不点任何东西,
      //   教练框的下拉就自己弹开了。开关默认 false, 只有本实例显式开。
      //   ⚠️ 只给 normalPicker 开: trialPicker 走的是 v0.17.0/1 已 V 测过的路径, 不碰。
      //   ⚠️ 绝不能在 LessonEditorModal 里定义 onready() —— 子类方法会遮蔽
      //   Modal.prototype 上那条 patch, 正好把 ziven-core 的 iOS 修复废掉。
      suppressAutofocusOpen: true,
      fields: {
        normal_coach_id: {
          pick: () => this.coaches,
          idOf: c => c.id || c.user_id,
          bare: true,
          placeholder: '输入教练姓名筛选…',
          emptyText: '当前校区下没有教练'
        },
        normal_student_id: {
          pick: () => this.students,
          idOf: s => s.user_id || s.id,
          bare: true,
          placeholder: '输入学员姓名筛选…',
          emptyText: '当前校区下没有学员'
        }
      }
    });

    // v0.6.8b — edit mode 学员卡 dropdown 联动 (fire-and-forget, loadStudentCards 异步)
    //   v0.8.8 — 调用点从 form state 建之前挪到之后:
    //   loadStudentCards 拿到卡列表后会回头把 id-only 占位升级成完整卡, 并顺手再跑一次
    //   类型/分类 fallback (lesson.student_card 缺失时只能等这一步才有 template)。
    //   form state 还没建时访问 this.form 会炸, 所以必须在下面 this.form 赋值之后再调。
    if (this.mode === 'edit' && initialStudent) {
      this.loadStudentCards(initialStudent);
    }

    // v0.6.7 — LessonOptions 启动期预加载 (内置 fallback 兜底, 跟 CardTemplateEditorModal 同款)
    LessonOptions.load();
    this.load();
  }
  className() {
    return 'Modal--medium ZS-BookingModal';
  }

  // ─── v0.17.0 双 tab 草稿 ──────────────────────────────────────────
  /**
   * 当前 tab 的表单草稿。
   *
   * 全文件所有 `this.form.xxx` 的读写都落到这里 —— 包括 computeTimeSlots /
   * roomDropdown / coachDropdown / validate / submit 这些**既有**方法,
   * 它们一行都不用改就同时服务两个 tab。
   * ⚠️ 只有 getter, 没有 setter: 谁要是写 `this.form = ...` 会在 strict mode 下抛错,
   *   这正是我们想要的 (那种写法会把两个 tab 的草稿合并掉)。要改就去改 this.drafts[tab]。
   */
  get form() {
    return this.drafts[this.tab];
  }

  /** 切 tab。**不搬字段** —— 各自草稿天然隔离, 切回来内容还在。 */
  onTabSwitch(tab) {
    if (this.tab === tab) return;
    this.tab = tab;
    // 切走时清掉红字/错误残留, 否则「正课没选学员」的红字会跟着跑到体验课 tab 上
    this.error = '';
    // v0.17.1 — 经办人候选懒加载 (辉哥 10/12 07:25 拍板)
    //   只在**真切到体验课 tab** 时才拉 /users?role=coach,sales。
    //   ⚠️ 放在 m.redraw() **之前**调: 首帧就该进入「候选加载中」态, 下拉框能立刻显示
    //     并 disable/显示 loading, 而不是先渲染一个能点的空下拉、候选到位后才突然变满。
    //     切回 'normal' 什么都不做 (正课 tab 用不到经办人)。
    if (tab === 'trial') this.ensureTrialPickerLoaded();
    m.redraw();
  }

  /**
   * v0.17.1 — 确保经办人候选已加载过 (幂等 + 防并发)
   *
   * ## 为什么要懒加载
   *   改前这行在 load() 里**无条件** await, 于是只约正课的户 (绝大多数) 打开 modal
   *   也要白白多付一次 /users?role=coach,sales 往返, 拖慢首屏。
   *
   * ## 为什么要 in-flight 守卫 (不能只靠 loaded 布尔)
   *   loaded 只在**请求结束后**才置位。用户快速来回切 tab 时, 第一次请求还在飞,
   *   loaded 还是 false —— 只判 loaded 就会连发第 2、第 3 次请求。
   *   所以把 promise 本身存下来: 飞行中的任何一次切 tab 都拿回**同一个** promise,
   *   天然只发一次请求。
   *
   * ## 为什么拉完要 m.redraw()
   *   loadAll() 只填候选数组, 不触发重绘 —— 不补这一次, 列表要等下一次交互才有内容。
   *
   * @returns {Promise|null} null = 已加载过, 无事可做
   */
  ensureTrialPickerLoaded() {
    if (this.trialPickerLoaded) return null; // 幂等: 拉过就不再请求
    if (this.trialPickerInFlight) return this.trialPickerInFlight; // 并发: 复用同一轮

    this.trialPickerInFlight = (async () => {
      try {
        await this.trialPicker.loadAll();
      } catch (e) {
        // 候选拉不到不阻塞其它字段 (跟 v0.17.0 同一处置)
      } finally {
        this.trialPickerLoaded = true;
        this.trialPickerInFlight = null;
        m.redraw(); // 候选就绪, 补一次重绘
      }
    })();
    return this.trialPickerInFlight;
  }
  title() {
    return this.mode === 'edit' ? '编辑课程' : '约课';
  }

  /**
   * v0.9.1 待补 4 (辉哥 V 测) — 「仅改这一次」作用范围顶部提示条
   *
   * 只在 edit mode 且这节课属于固定课时渲染:
   *   · 普通约课 → 整个系列的概念不存在, 显示会误导用户以为有连带影响
   *   · 固定课课次 → 必须说明「只改全系列里的这一天, 其他日期不动」
   *
   * 为什么不复用 FixedLessonModal: 那是右键空槽「新建固定课」用的另一个弹窗
   * (v0.18.39 起它已经是 mithril Modal 子类, 见 modal/FixedLessonModal.js),
   * 这里改的是已存在的单次课次, 两者不是同一个动作。
   */
  scopeBanner() {
    const lesson = this.attrs && this.attrs.lesson;
    if (this.mode !== 'edit' || !lesson) return null;
    if (!lesson.fixed_lesson_id) return null;

    // occurrence_date = 这一次的日期 (YYYY-MM-DD); 缺失时退回 modal 上的日期 chip
    const raw = String(lesson.occurrence_date || this.form && this.form.dateLabel || '');

    // ⚠️⚠️ 这里**完全不引入局部变量**, 格式转换抽成模块级函数。
    //   原因: 本仓的 JSX 编译链 (flarum-webpack-config + terser) 会把 JSX 元素编译成
    //   `m("div", {...}, ...)` 这种调用, m/s 都是 JSX pragma。
    //   我第一版写 `const m = /regex/.exec(...)`, terser 把 m 压成了 `s`,
    //   于是 `s("div", ...)` 实际在调用那个正则匹配结果 (首次为 null),
    //   抛 `TypeError: s is not a function` → **整个约课 modal 弹不出来**,
    //   比「少一条提示条」严重得多。
    //   第二版换成长名 dateMatch 后, terser 仍会把它压回单字母, 问题复现。
    //   结论: JSX 附近的局部变量一律不进 JSX 作用域, 逻辑放模块级函数。
    const pretty = formatScopeDate(raw);
    const mainLine = '本次保存只改全系列里的这一天（' + pretty + '），其他日期不动';
    const subLine = '想让整个每周系列一起改，请切到「固定课」模式后拖动课块（会同步所有未来课次）。';
    return m("div", {
      className: "ZS-scope-banner"
    }, m("div", {
      className: "ZS-scope-title"
    }, m("i", {
      className: "fas fa-info-circle"
    }), m("strong", null, mainLine)), m("div", {
      className: "ZS-scope-sub"
    }, subLine));
  }

  // ─── Helpers ────────────────────────────────────────────────

  formatDate(d) {
    // "2026-09-26"
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  formatDateLabel(d) {
    // "2026年9月26日 周六"
    const weekday = ['日', '周一', '周二', '周三', '周四', '周五', '周六'][d.getDay()];
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${weekday}`;
  }
  formatHHMM(d) {
    // "10:00"
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }

  // ─── v0.18.35 日期选择器 (正课 / 体验课两个 tab 共用) ────────────────────

  /**
   * v0.18.35 — Row 2 的「日期」控件。
   *
   * ## 两条路径 (辉哥 10/20 拍板)
   *   · lockDate === true  (fc 课表入口) ⇒ **只读 chip**, 与改动前**逐字相同**
   *     (同一个 span / 同一个 class / 同一个文案, 不加任何可编辑暗示)。
   *   · lockDate === false (「约课管理」的「+ 新建预约」) ⇒ **可编辑日期框** (flatpickr)。
   *
   * ## 为什么是 `<input type="text" readonly>` 而不是原生 `<input type="date">`
   *   - 原生 date 控件的弹层宽度由浏览器定死 (Chrome ~250px), 不可控 ⇒ 本仓 BookingModal
   *     早就踩过这个坑, 一律走 flatpickr。
   *   - `readonly` + flatpickr 的 `allowInput` 默认 false ⇒ 用户**不能手输**日期,
   *     只能从日历里点 (手输会绕过「补录过去」以外的一切校验口径)。
   *
   * ## v0.18.36 (辉哥 10/20 拍板) — 改成跟「操作日志」tab **完全同款**
   *   · class: `ZS-form-input` → `ZS-date-input-text` (底色/边框/圆角/字号/padding 全走
   *     操作日志 tab 那套共用样式)。
   *   · 显示值: 中文 `2026年10月20日 周二` → **ISO `2026-10-20`** (跟操作日志 tab 逐字一致)。
   *
   * ### 为什么显示值改回 ISO 之后, `syncDateInputDisplay` / `onValueUpdate` 整条都删了
   *   改动前那套「flatpickr 写 ISO ⇒ 我们再把 input 改回中文」是**为了显示中文**而存在的。
   *   现在显示的就是 ISO ⇒ flatpickr 写的值跟我们要显示的值**本来就是同一个** ⇒
   *   那套「写完再纠正」的机制成了纯多余 (而且它每次都要直写 DOM, 反而多一个失配来源)。
   *   ⇒ `syncDateInputDisplay()` 方法整个删除, `onValueUpdate` 钩子不再传。
   *
   * ### `form.dateLabel` 为什么还留着
   *   它**不再是显示值**, 但仍然有两个用途:
   *   · 锁死路径的只读 chip (`<span class="ZS-date-chip">{f.dateLabel}</span>`) —— 一行没动。
   *   · `scopeBanner()` 的兜底文案 (固定课「本次保存只改这一天」)。
   *   ⇒ `applyDate()` 那个「date 与 dateLabel 一起改」的统一入口**保留不动**。
   */
  renderDateField(draftKey) {
    const f = this.drafts[draftKey];
    // 🔒 锁死路径 —— 与改动前逐字相同 (只读 chip, 无 onclick / 无可编辑暗示)
    if (this.lockDate) {
      return m("span", {
        className: "ZS-date-chip"
      }, f.dateLabel);
    }
    return m("input", {
      // v0.18.36 — `ZS-date-input-text` = 操作日志 tab 的日期框同款 class。
      //   ⚠️ `ZS-booking-date-input` **必须保留**: 它是 LESS `:has()` 选择器
      //     (`.ZS-time-row:has(.ZS-booking-date-input)`) + JS 定位
      //     (querySelector('.ZS-booking-date-input')) 的锚点, 删了会连锁崩掉。
      className: "ZS-date-input-text ZS-booking-date-input",
      type: "text",
      readonly: true
      // v0.18.36 — 显示值 = ISO (`2026-10-20`), 跟操作日志 tab 逐字一致。
      ,
      value: f.date,
      placeholder: "\u9009\u62E9\u65E5\u671F"
    });
  }

  /**
   * v0.18.35 — 改日期的**唯一**入口 (正课 / 体验课两份草稿都走它)。
   *
   * ⛔ `date` 与 `dateLabel` **必须同时改**, 少改一个就是「显示 A、存 B」的数据错位:
   *   · `form.date`      = `YYYY-MM-DD`  ⇒ 提交时拼 `start_at` / `end_at`
   *     (正课 L~2000 `${f.date}T${f.startTime}:00`、体验课同款、碰撞预览同款)
   *   · `form.dateLabel` = `2026年10月20日 周二` ⇒ 锁死路径的只读 chip + scopeBanner 兜底文案
   *     (v0.18.36 起**不再**是日期框的显示值 —— 显示值改成了 ISO, 见 renderDateField)
   * 所以这里**只认一个入口**: 任何改日期的地方都必须调它, 不许直接赋值 `f.date`。
   */
  applyDate(draftKey, dateObj) {
    const f = this.drafts[draftKey];
    if (!f || !dateObj) return;
    f.date = this.formatDate(dateObj);
    f.dateLabel = this.formatDateLabel(dateObj);
  }

  /**
   * v0.18.35 — `YYYY-MM-DD` → 本地零点的 Date (给 flatpickr 的 defaultDate 用)。
   *
   * ⚠️ 必须用**本地**构造 (`new Date(y, m-1, d)`) 而不是 `new Date('2026-10-20')`:
   *   后者按 UTC 解析, 在 UTC+8 会变成 10/20 08:00 (虽然日期数字对得上, 但
   *   flatpickr 内部按本地 getDate() 取值, 跨时区会差一天)。本地构造跟
   *   formatDate()/formatDateLabel() 用的是同一套本地 getter ⇒ 往返**逐字一致**。
   */
  dateObjFromIso(iso) {
    const p = String(iso || '').split('-');
    if (p.length !== 3) return new Date();
    const y = +p[0];
    const mo = +p[1] - 1;
    const d = +p[2];
    if (!y || isNaN(mo) || isNaN(d)) return new Date();
    return new Date(y, mo, d);
  }

  /**
   * v0.18.36 — ~~把日期框的显示值拉回中文格式~~ ⇒ **本方法整体删除**。
   *
   * ## 为什么删
   *   它存在的唯一目的是: flatpickr 写进来的是 ISO (`dateFormat: 'Y-m-d'`), 而我们要显示
   *   中文 (`2026年10月20日 周二`) ⇒ 每次 flatpickr 改写 input.value 之后都要**直写 DOM**
   *   把它纠正回中文 (onValueUpdate + onChange 两个钩子都调它)。
   *
   *   v0.18.36 起显示值**改成了 ISO** (跟「操作日志」tab 完全同款) ⇒
   *   flatpickr 写的值 == 我们要显示的值 ⇒ 这套「写完再纠正」的机制**没有存在意义了**,
   *   留着反而多一个失配来源 (直写 DOM 跟 mithril 托管的 value 抢活)。
   *
   * ## `form.dateLabel` 为什么没跟着删
   *   它不再是显示值, 但锁死路径的只读 chip (`<span class="ZS-date-chip">`) 跟
   *   `scopeBanner()` 兜底文案还在用它 ⇒ 字段保留, `applyDate()` 照旧一起更新。
   */

  /** 当前 tab 的草稿 key (正课 'normal' / 体验课 'trial') */
  currentDateDraftKey() {
    return this.tab === 'trial' ? 'trial' : 'normal';
  }

  /**
   * v0.18.35 — 幂等挂载 flatpickr (跟 CalendarTab.initDatePicker 同款思路)。
   *
   * 调用点: oncreate (首帧) + onupdate (每次 patch 后)。**不是**每次 redraw 都重新 init:
   *   · 已绑在**当前这个** input 上 ⇒ 直接返回 (幂等守卫)。
   *   · 绑在**别的/旧的** input 上 (切 tab ⇒ 整棵 content() 换了, input 是新 DOM)
   *     ⇒ 先 destroy 旧的再挂新的。
   *   · 正在挂载中 (vendor 异步, 最多等 5s) ⇒ 记下目标节点, 在途那次结束后自动复查。
   */
  ensureDatePicker() {
    const dp = this._dp;
    // 锁死 ⇒ 根本没有日期框 ⇒ 什么都不做 (也保证 fc 课表入口一个 flatpickr 都不挂)
    if (this.lockDate) return;
    const el = this.element ? this.element.querySelector('.ZS-booking-date-input') : null;
    if (!el) {
      // 输入框不在了 (切走了 / modal 正在拆) ⇒ 把旧实例销毁, 免得留个孤儿 calendar
      dp.wantEl = null;
      if (dp.instance) destroyDatePicker(this._dp);
      return;
    }
    dp.wantEl = el;
    // 已经绑在当前这个 input 上且它还在 DOM 里 ⇒ 已经是想要的状态, 什么都不做
    if (dp.instance && dp.el === el && el.isConnected) return;
    // 实例还绑在旧的/别的节点上 ⇒ 先销毁, 避免两个实例抢同一个输入框
    if (dp.instance) destroyDatePicker(this._dp);
    // 同一个节点挂不上 (vendor 没就绪) ⇒ 不重复试 (否则每次 redraw 都白等 5s)
    if (dp.failedEl === el) return;
    // 已有一次在途挂载 ⇒ 等它结束 (结束时会按 wantEl 自动复查)
    if (dp.mounting) return;
    this.mountDatePickerOn(el);
  }

  /** 真正去挂 (异步: mountDatePicker 内部要等 vendor) */
  async mountDatePickerOn(el) {
    const dp = this._dp;
    dp.mounting = true;
    const key = this.currentDateDraftKey();
    const f = this.drafts[key];
    let instance = null;
    try {
      instance = await mountDatePicker(el, {
        mode: 'date',
        // v0.18.36 — 跟「操作日志」tab **完全同款**的 flatpickr 配置。
        //
        // ## `dateFormat: 'Y-m-d'` 为什么这里要单独钉死 (helper 默认是 'Y/m/d')
        //   helper 的默认 `dateFormat` 是 `'Y/m/d'` (斜杠), 4 个调用方都靠它。
        //   本调用点如果跟着用斜杠, 会出现「显示 A、存 B」:
        //     · flatpickr 写进 input 的是 `2026/10/27` (斜杠)
        //     · mithril 托管的 `value={f.date}` 是 `2026-10-27` (短横线)
        //     · 两者不一致 ⇒ 用户选完日期会先看到一帧斜杠, 且跟操作日志 tab 不一致。
        //   ⇒ 在**本调用点**单独传 `dateFormat: 'Y-m-d'`:
        //     · flatpickr 写的值 == mithril 托管的值 == 操作日志 tab 的显示格式 ⇒ 逐字一致;
        //     · **不动 helper 的默认值** ⇒ 另外 4 个调用方 (CalendarTab / OperationLogsTab /
        //       SalesTab / StatsTab) 零回归;
        //     · 不用「onChange 里 replace(/\//g,'-')」那种事后纠正 —— 那样只是把不一致
        //       藏起来 (还会跟 flatpickr 内部的 dateFormat 分道扬镳, 正是 v0.18.35
        //       「显示 A、存 B」的老毛病)。直接在源头对齐 = 零失配。
        //   ⚠️ **不加 minDate**: 内部员工可能要**补录过去**的课
        //      (BindCardModal 的 minDate:'today' 是「开卡过期时间」场景, 语义不同, 不能照抄)。
        dateFormat: 'Y-m-d',
        // flatpickr 内部的初始选中日期 = 当前草稿的日期 (本地零点, 跟 formatDate 同口径)
        defaultDate: this.dateObjFromIso(f.date),
        // v0.18.36 — ⛔ `allowInput` / `disableMobile` **不再传**, 跟操作日志 tab 逐项同款:
        //   · `allowInput` 默认就是 false (不许手输) —— 跟操作日志 tab 一致, 不用显式写。
        //   · `disableMobile` 走 helper 默认 `!isMobileUA`:
        //     ⚠️ **取舍**: v0.18.35 在这里硬钉了 `disableMobile: true` (避免 flatpickr 在
        //       移动端克隆一个 native `<input type="date">`, DOM 里出现两个日期框)。
        //       为了跟操作日志 tab **完全同款**, 这里接受那个既有行为 ——
        //       操作日志 tab 一直是这么跑的, 不是本次新引入的缺陷。
        //       ⇒ 真机上 (iPhone/Android UA) 约课弹窗会出现「文本框 + 原生日期框」两个控件,
        //         本轮**按辉哥要求不加补丁**, 只如实记录。
        onChange: selectedDates => {
          if (!selectedDates || !selectedDates.length) return;
          // ⛔ 走统一入口: date 与 dateLabel 一起改, 杜绝「显示 A、存 B」
          this.applyDate(this.currentDateDraftKey(), selectedDates[0]);
          // v0.18.36 — 不用再 syncDateInputDisplay(): 显示值就是 ISO, flatpickr 写的
          //   值跟 mithril 托管的 `value={f.date}` 本来就一致, 没有要纠正的东西。
          m.redraw();
        }
      });
    } catch (e) {
      console.error('[booking-modal] date picker init failed', e);
      instance = null;
    }
    dp.mounting = false;
    if (!instance) {
      // vendor 没就绪 (helper 最多等 5s) ⇒ 记下这个节点, 同一个节点不再重复试
      dp.failedEl = el;
      return;
    }
    // 挂载期间可能切了 tab (input 换人) / 关了 modal (节点被摘) ⇒ 这一份作废
    if (!el.isConnected || dp.wantEl !== el) {
      try {
        instance.destroy();
      } catch (e) {/* noop */}
      retryDatePickerIfNeeded(this._dp, () => this.ensureDatePicker());
      return;
    }
    dp.instance = instance;
    dp.el = el;
    // v0.18.36 — 初始化时 flatpickr 往 input 写的已经是 ISO (`dateFormat: 'Y-m-d'`),
    //   跟 mithril 托管的 `value={f.date}` 一致 ⇒ 不需要任何纠正。
  }

  // ─── v0.8.8 edit mode 回显 helper ─────────────────────────────────

  /**
   * v0.8.8 — 教练/学员对象归一化 (辉哥 10/7 11:44 报障: 编辑约课时两个字段空白)
   *
   * 改前 oninit 只造 id-only 空壳 { display_name: '', username: '' },
   *   而下拉控件的 label = display_name || username → 两个都空 → 渲染空白。
   * 改后优先用 ListLessonsController 已经序列化好的完整对象 (username + display_name + avatar_url),
   *   同时补 user_id (Flarum User 主键字段名叫 id, 但 students-list 接口返的是 user_id,
   *   dropdown active 比较 L486 和 submit payload L630/632 都用 `user_id || id`, 补上更稳),
   *   对象缺失时才退回 id-only 占位保底。
   */
  normalizeUserObject(apiUser, fallbackId) {
    if (apiUser && apiUser.id) {
      return {
        ...apiUser,
        user_id: apiUser.id
      };
    }
    if (fallbackId) {
      return {
        id: fallbackId,
        user_id: fallbackId,
        display_name: '',
        username: ''
      };
    }
    return null;
  }

  /**
   * v0.8.8 — 学员卡对象归一化 (辉哥 10/7 11:44 报障: 学员卡字段显示 "#18" 不是卡名)
   *
   * 改前 oninit 只造 { id }, studentCardDropdown 的 label = name || template_name || `#${id}` → 只剩 "#18"。
   * 改后优先用 ListLessonsController 的 student_card (含 name = template.name + template 联表),
   *   跟 loadStudentCards 拿回来的 student-cards 列表项结构对齐 (都有 name / template.minutes /
   *   template.lesson_type_id / template.lesson_category_ids), 这样类型/分类 dropdown 的
   *   `card.template` 依赖在首帧就成立, 不用等异步。
   */
  normalizeCardObject(apiCard, fallbackId) {
    if (apiCard && apiCard.id) {
      return {
        ...apiCard
      };
    }
    if (fallbackId) {
      return {
        id: fallbackId
      };
    }
    return null;
  }

  /**
   * v0.8.8 — 课程类型取值: lesson 表列优先, NULL 时 fallback 学员卡 template
   * (v0.6.42 之前的历史约课两列是 NULL; card template 没配也才返 null → dropdown 显示 "未配置")
   * cardOverride: loadStudentCards 异步补上完整卡后调用时传 this.form.studentCard 覆盖 lesson 上的
   */
  resolveLessonType(lesson, cardOverride) {
    const saved = lesson && lesson.lesson_type_id;
    if (saved) return saved;
    const card = cardOverride || lesson && lesson.student_card;
    const tmpl = card && card.template;
    return tmpl && tmpl.lesson_type_id || null;
  }

  /**
   * v0.8.8 — 课程分类取值: lesson 表列优先, NULL 时 fallback 学员卡 template 的
   * lesson_category_ids JSON 数组**第一个** (跟 onStudentCardChange 的默认值口径一致)
   */
  resolveLessonCategory(lesson, cardOverride) {
    const saved = lesson && lesson.lesson_category_id;
    if (saved) return saved;
    const card = cardOverride || lesson && lesson.student_card;
    const tmpl = card && card.template;
    const catIds = tmpl && Array.isArray(tmpl.lesson_category_ids) ? tmpl.lesson_category_ids : [];
    return catIds.length > 0 ? catIds[0] : null;
  }

  // ─── Load ──────────────────────────────────────────────────

  async load() {
    try {
      const tasks = [
      // 教室列表 (按 active school)
      Api.list('rooms', this.activeSchoolId ? {
        school_id: this.activeSchoolId
      } : {}),
      // 教练列表 (按 active school, role=coach)
      Api.list('users', {
        role: 'coach',
        ...(this.activeSchoolId ? {
          school_id: +this.activeSchoolId
        } : {})
      }),
      // 学员列表 (默认拿前 200)
      Api.get('students-list', {
        ...(this.activeSchoolId ? {
          school_id: this.activeSchoolId
        } : {}),
        limit: 200
      }),
      // ── v0.17.0 体验课 tab 的三个候选源 ──────────────────────────
      //   卡模板: 下拉候选 = builtin-trial 且 lessons===1 (方案 B: 选**模板**, 约课时物化)
      //   ⚠️ 候选**不过滤有效期**: 模板没有有效期概念, 物化出来的卡 expires_at=NULL。
      //   分类 / 渠道: 跟 SalesFollowupCreateModal 同款端点。
      Api.list('card-templates', {
        ...(this.activeSchoolId ? {
          school_id: +this.activeSchoolId
        } : {})
      }).catch(() => []), Api.get('admin/lesson-options').catch(() => ({
        categories: []
      })), Api.get('admin/lead-channels').catch(() => ({
        channels: []
      }))];
      const [rooms, coaches, studentsRes, templates, opts, chans] = await Promise.all(tasks);
      this.rooms = rooms || [];
      this.coaches = coaches || [];
      this.students = studentsRes && studentsRes.students || [];
      // v0.17.12 — 教练/学员候选此刻才就位, 同步灌进 normalPicker。
      //   cfg.pick 分支里没有 await, 所以这一句执行完 st.list 就已经是最终值。
      //   不补这次, 首帧渲染出的是一个**永久空**的候选列表 (picker 只在 load() 里拿一次)。
      await this.normalPicker.loadAll();
      this.cardTemplates = templates || [];
      this.categories = opts && opts.categories || [];
      this.channels = chans && chans.channels || [];
      // v0.17.1 — 经办人候选**不再**在这里无条件拉 (辉哥 10/12 07:25 拍板):
      //   它只被体验课 tab 用到, 只约正课的户 (绝大多数) 不该为此多付一次
      //   /users?role=coach,sales 往返。改由 onTabSwitch → ensureTrialPickerLoaded 懒加载,
      //   幂等 + 防并发守卫都在那边。正课 tab 的首屏渲染因此零变化、也少一个 await。
      m.redraw();
    } catch (e) {
      console.error('[booking-modal] load failed', e);
    }
  }

  // v0.17.0 — 可搜索下拉的挂载/销毁跟 SalesFollowupCreateModal 同款。
  //   mount/afterUpdate/destroy 必须成对, 否则 modal 关掉后 document 监听还活着,
  //   每次全站 mousedown 都会跑进一个已死实例 (跟 v0.16.7 同一个坑)。
  oncreate(vnode) {
    super.oncreate(vnode);
    this.trialPicker.mount();
    this.normalPicker.mount();
    // ── v0.18.38 (辉哥 10/20 拍板) — 默认就停在「体验课」tab 时, 经办人候选必须**在这里**拉起来 ──
    //
    // ⚠️ 为什么不能靠「先设 this.tab='trial' 再调 onTabSwitch('trial')」:
    //   onTabSwitch 第一行是 `if (this.tab === tab) return;` —— tab 已经是 'trial' 时会**早退**,
    //   里面的 `ensureTrialPickerLoaded()` 根本不会跑 ⇒ 体验课 tab 的「经办人」下拉是**空的**。
    //
    // ⚠️ 为什么放在 oncreate (而不是 oninit / load()):
    //   · oninit 阶段 modal 还没进 DOM (trialPicker 那时刚 new 出来), 拉候选没有意义;
    //   · oncreate 是「组件已挂载」的第一个点 —— 此刻拉, 候选请求与 load() 里那 6 个请求
    //     (教室/教练/学员/卡模板/选项/渠道) **并行**, 用户看到弹窗时经办人基本已就位;
    //     若塞进 load() (那 6 个请求 Promise.all **之后**), 就变成**串行** ⇒ 白等一个来回。
    //
    // ⚠️ 为什么不怕 ensureTrialPickerLoaded() 内部的 m.redraw() 太早:
    //   那个 m.redraw() 位于 async IIFE 的 finally 里 —— 是**请求 resolve 之后**的微任务,
    //   那一刻不在 mithril 的渲染周期里, 重绘安全。
    //   (真在渲染周期里调 m.redraw() 会被 mithril 吞掉, 候选就永远刷不出来。)
    //
    // ⚠️ 幂等: ensureTrialPickerLoaded() 自带 loaded / in-flight 双守卫 ⇒ 用户之后切走再切回
    //   体验课 tab 时 (onTabSwitch) 不会再发第二遍请求。
    if (this.tab === 'trial') this.ensureTrialPickerLoaded();
    // v0.18.35 — 首帧就把日期框挂上 flatpickr (锁死入口没有日期框 ⇒ 这里什么都不做)
    this.ensureDatePicker();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    // 候选列表是这轮 patch 才插进 DOM 的, 需要重新判定要不要向上翻
    this.trialPicker.afterUpdate();
    this.normalPicker.afterUpdate();
    // v0.17.75 — 表单一变就排一次 300ms 防抖的碰撞预览 (签名守卫在 scheduleLiveCheck 里)
    this.scheduleLiveCheck();
    // v0.18.35 — 日期框的幂等挂载守卫放这里:
    //   切 tab ⇒ content() 整棵换掉 ⇒ 日期框是**新的 DOM 节点** ⇒ 这里负责把
    //   flatpickr 从旧节点挪到新节点 (幂等: 已经绑在当前节点上就直接返回)。
    this.ensureDatePicker();
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.trialPicker.destroy();
    this.normalPicker.destroy();
    // v0.18.35 — flatpickr 必须跟 modal 同生共死:
    //   不 destroy 的话, document.body 上会留一个孤儿 .flatpickr-calendar,
    //   而且它的 document 监听 (mousedown/touchstart) 会一直活着。
    destroyDatePicker(this._dp);
    // v0.17.75 — modal 关了就把还挂着的防抖定时器撤掉, 否则它会朝一个已死的实例发请求
    if (this._liveTimer) {
      clearTimeout(this._liveTimer);
      this._liveTimer = null;
    }
    // v0.18.86 (P2-4) — 同款: 体验跟进的查重定时器也得撤
    if (this._trialFollowupTimer) {
      clearTimeout(this._trialFollowupTimer);
      this._trialFollowupTimer = null;
    }
  }

  // ─── v0.17.75 实时碰撞预览 ────────────────────────────────────────

  /**
   * 当前 tab 下「影响碰撞结果的字段」的签名。
   *
   * 为什么用签名守卫而不是在每个 setter 里手调 scheduleLiveCheck():
   *   这些字段散落在 roomDropdown / coachDropdown / timeSlotDropdown / normalPicker / trialPicker
   *   十几处 oninput 里, 逐个插钩子 = 十几处改动 + 以后新增字段**必然**漏。
   *   签名比对放在 onupdate 这一个点, 新字段加进来只要写进这个数组就不会漏, 且天然幂等:
   *   「没变就不重发」⇒ m.redraw() 不会自激成死循环 (m.redraw → onupdate → 改 state → 再 redraw)。
   */
  liveCheckSignature() {
    if (this.tab === 'trial') {
      const t = this.drafts.trial;
      return ['trial', this.activeSchoolId, t.room && t.room.id, t.coach && (t.coach.id || t.coach.user_id), t.date, t.startTime, t.endTime, t.scheduleNodeId].join('|');
    }
    const f = this.form;
    return ['normal', this.activeSchoolId, f.room && f.room.id, f.coach && (f.coach.id || f.coach.user_id), f.student && (f.student.user_id || f.student.id), f.studentCard && f.studentCard.id, f.date, f.startTime, f.endTime, f.scheduleNodeId, this.lesson && this.lesson.id].join('|');
  }

  /** 排一次 300ms 防抖 (与 LessonsTab 搜索防抖同毫秒数 = 一套手感) */
  scheduleLiveCheck() {
    // 保存中 ⇒ 不发 (正在提交, 表单状态由保存流程自己管)
    // ⚠️ 这里**故意不**判断 Modal.DISMISSING: Flarum 2.0 的 Modal 没有这个静态常量
    //   (弹窗生命周期状态是 vnode.attrs.state, 由 ModalManager 传进来), 写
    //   `this.state === Modal.DISMISSING` 会退化成 `undefined === undefined` ⇒ **恒真**
    //   ⇒ 预览永远不触发。关闭态由 onremove() 清定时器兜住, 那才是可靠的那个点。
    if (this.saving) return;
    const sig = this.liveCheckSignature();
    if (sig === this._liveSig) return; // 表单没变 ⇒ 不重发 (也是防自激的关键)
    this._liveSig = sig;
    if (this._liveTimer) clearTimeout(this._liveTimer);
    this._liveTimer = setTimeout(() => {
      this._liveTimer = null;
      this.runLiveCheck();
    }, 300);
  }
  async runLiveCheck() {
    // 表单还没填完 (validate 不过) ⇒ 没什么可查的, 直接清空提示。
    //   ⚠️ 这条 gate 很重要: 半填的表单会拿 coach_id=0 / room_id=0 去查, 那是**噪音**不是提示。
    const err = this.tab === 'trial' ? this.trialValidate() : this.validate();
    if (err) {
      this._liveSig = null; // 让填完之后的下一次 redraw 能重新触发
      this.liveCheck = {
        conflict: null,
        warnings: [],
        pending: false
      };
      m.redraw();
      return;
    }
    const seq = ++this._liveSeq;
    this.liveCheck = {
      ...this.liveCheck,
      pending: true
    };
    const isTrial = this.tab === 'trial';
    let attrs;
    if (isTrial) {
      const t = this.drafts.trial;
      attrs = {
        school_id: +this.activeSchoolId,
        room_id: +t.room.id,
        coach_id: +(t.coach.id || t.coach.user_id),
        // 体验课没有「学员」概念 (学员是新建的), 保持与 CreateTrialLessonController 一致:
        //   student_id=0 ⇒ student 那条查询命中不到任何行, 等于自动跳过
        student_id: 0,
        student_card_id: null,
        start_at: `${t.date}T${t.startTime}:00`,
        end_at: `${t.date}T${t.endTime}:00`,
        schedule_node_id: t.scheduleNodeId != null ? +t.scheduleNodeId : null
      };
    } else {
      const f = this.form;
      attrs = {
        school_id: +this.activeSchoolId,
        room_id: +f.room.id,
        coach_id: +(f.coach.id || f.coach.user_id),
        student_id: +(f.student.user_id || f.student.id),
        student_card_id: f.studentCard ? +f.studentCard.id : null,
        start_at: `${f.date}T${f.startTime}:00`,
        end_at: `${f.date}T${f.endTime}:00`,
        schedule_node_id: f.scheduleNodeId != null ? +f.scheduleNodeId : null
      };
      // edit 模式带上 id ⇒ 后端排除自己, 不会把自己判成冲突 (与 v0.6.44 同一条口径)
      if (this.mode === 'edit' && this.lesson && this.lesson.id) attrs.id = this.lesson.id;
    }
    // v0.17.75 — 要课槽占用这条**预览**, 得自己开口要 (日历不传 ⇒ 日历行为一行不变)。
    //   否则用户点保存才被拒, 而弹窗事前一声不吭 —— 那比现在更费解。
    attrs.include_slot_occupancy = true;
    try {
      const r = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/lessons/check-conflict',
        body: {
          data: {
            attributes: attrs
          }
        },
        serialize: x => JSON.stringify(x),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (seq !== this._liveSeq) return; // 过期响应, 丢掉
      this.liveCheck = {
        conflict: r && r.conflict || null,
        // ⚠️ 加字段不是改字段: 老响应没有 warnings 时按空数组处理, 不炸
        warnings: r && Array.isArray(r.warnings) ? r.warnings : [],
        pending: false
      };
    } catch (e) {
      if (seq !== this._liveSeq) return;
      // 预览失败**不打扰用户**: 静默收空。真正的判定在保存那一步, 那里本来就有红字兜底。
      this.liveCheck = {
        conflict: null,
        warnings: [],
        pending: false
      };
      console.warn('[booking-modal] live conflict check failed', e);
    }
    m.redraw();
  }

  /**
   * v0.17.75 — 实时碰撞提示 (红 / 黄两块, **互斥语义**)。
   *
   * ⚠️ 黄色这一块**绝不能**阻止提交: 本方法只渲染, 不碰 `this.saving`, 也不碰任何
   *   `disabled` —— 保存按钮的 disabled 只由 `this.saving` 决定 (见 content() 页脚)。
   *
   * 称呼: 教练 / 教室 / 学员 —— 三种 type 各叫各的, 不复用同一个词。
   */
  liveConflictNotice() {
    const c = this.liveCheck;
    if (!c || !c.conflict && !(c.warnings && c.warnings.length)) return null;
    return m("div", null, c.conflict && m("div", {
      className: "ZS-live-conflict"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), m("span", null, c.conflict.message, m("br", null), m("strong", null, "\u8FD9\u6761\u4F1A\u62E6\u4F4F\u4FDD\u5B58"), ", \u8BF7\u6309\u63D0\u793A\u8C03\u6574\u540E\u518D\u4FDD\u5B58\u3002")), c.warnings && c.warnings.length > 0 && m("div", {
      className: "ZS-live-warn"
    }, m("i", {
      className: "fas fa-circle-info"
    }), m("div", null, m("strong", null, "\u4FDD\u5B58\u4E0D\u53D7\u5F71\u54CD"), " \xB7 \u4EE5\u4E0B ", c.warnings.length, " \u6761\u53EA\u662F\u63D0\u793A:", m("ul", {
      className: "ZS-live-warn-list"
    }, c.warnings.map((w, i) => m("li", {
      key: (w.type || 'w') + '-' + (w.lesson_id || i)
    }, w.type === 'coach' ? '教练' : w.type === 'room' ? '教室' : '学员', ":", w.message))))));
  }

  // ─── 时间联动 ─────────────────────────────────────────────────
  //
  // v0.17.5 — 结束时间**一律 = 开始时间所在区块的尾**, 不再看卡模板的 minutes。
  //   辉哥 10/12 原话: 「约课的时候, 不要有自定义时间区间, 每一节课, 不管它课时多长,
  //   都是占一个时间区块」。
  //
  //   这条不变式同时解决了三件事:
  //   1. 下拉永远命中某个真实节点 ⇒ 不再需要合成 "HH:MM-HH:MM (自定义)" 假项;
  //   2. [CUSTOM] 提示失去唯一的触发条件 (endTime ≠ slot.endTime) ⇒ 提示整段删除;
  //   3. 后端 LessonConflictChecker 的时长校验 (business_hours_duration, 要求
  //      end-start **恒等于** lesson_duration_minutes) 自然通过 —— 改前 30 分钟的
  //      体验卡 (minutes=30) 在后端是直接 422, 根本存不进去。
  //
  //   ⚠️ 删掉的两个 handler (v0.6.7 遗留) 此前已经是**零调用**的死代码:
  //      onStartTimeChange / onEndTimeChange —— 全仓库 (js/src + PHP) grep 只有定义处,
  //      因为 v0.7.1 把「两个 input」改成了「单下拉」, 结束时间不再由用户直接输入。
  //      它们唯一的作用就是维护 endTimeAuto, 那个状态位随 CUSTOM 提示一起退休。

  /**
   * 找「当前选中的那个时间段」(**不看 endTime**)。
   *
   * v0.17.62 — **优先按 scheduleNodeId 匹配**, 没有才退回按 startTime 匹配。
   * 为什么不能只看 startTime: 现在允许选**后端明确指定的那个节点**,
   * 而下拉里每一项都带 nodeId, 按 id 比才不会认错。
   * ⚠️ 注意 startTime 在**当前**规则 (start 严格递增) 下仍然唯一, 所以两条路径
   *   在不重叠的排版上结果一样; 留 nodeId 这条是为了「用户选了 B」时不靠巧合对上。
   * 都匹配不到 → 返 null (如实显示「请选择时间段」, 不静默改用户填的值)。
   */
  slotByStartTime(startTime) {
    const slots = this.computeTimeSlots();
    const wantedId = this.form && this.form.scheduleNodeId != null ? +this.form.scheduleNodeId : null;
    if (wantedId != null) {
      const byId = slots.find(s => s.nodeId != null && +s.nodeId === wantedId);
      if (byId) return byId;
    }
    if (!startTime) return null;
    return slots.find(s => s.startTime === startTime) || null;
  }

  /**
   * 结束时间 = 开始时间 + **卡模板的课时长度**。
   *
   * ## 为什么是卡时长, 不是时间段的末尾 (v0.17.57 改回)
   *   时间段是**纯标记** —— 它只说明「这一行代表哪段时间」, 不约束课有多长 (老板拍板第 2 条)。
   *   所以选一个时间段只能定「几点开始」, 定不了「几点结束」。
   *   结束时间由卡模板的 `template.minutes` 决定 (设计稿 §3: 「30 分钟的课 + 14:00 那段
   *   = 14:00–14:30」)。
   *
   * ⚠️ 改这里之前是 `endTime = slot.endTime` (v0.17.5「每节课一律占一个完整时间区块」那个
   *   时代的老规则)。新规则下时间段的跨度是**小时级**的 (09:00–12:00 / 13:00–18:00),
   *   沿用 slot.endTime 会让每个用户随手点一下就约出一节 3~5 小时的课 —— 那不是「区块」,
   *   那是把整段时间都占了。
   *
   * 卡没配 minutes (理论上不该发生: validate() 强制先选卡) → 退回该时间段的跨度,
   *   再不行就保持原 endTime 不动, **不在这里瞎猜一个值**。
   */
  recalcEndFromCard() {
    const card = this.form.studentCard;
    const slot = this.slotByStartTime(this.form.startTime);
    let minutes = 0;
    if (card && card.template && +card.template.minutes > 0) {
      minutes = +card.template.minutes;
    } else if (slot) {
      minutes = this.hhmmToMinutes(slot.endTime) - this.hhmmToMinutes(slot.startTime);
    }
    if (!(minutes > 0)) return false;
    this.form.endTime = this.minutesToHHMM(this.hhmmToMinutes(this.form.startTime) + minutes);
    return true;
  }

  // v0.17.57 — 读校区课表 (GET /schools/{id}/schedule), 取当前生效版本的时间段清单。
  //   异步 fetch, 完成 → m.redraw() 触发 timeSlotDropdown 重渲染
  //   失败 / 没配课表 / 课表全过期 → 静默兜底 (scheduleNodes = null), dropdown 显示占位提示
  //   SOP 108: fire-and-forget, 不抛 Promise rejection
  //
  // ⚠️ v0.17.57 — 旧版这里还会 `this.alignDraftsToSlots()`, 把两份草稿的历史时间**静默**
  //   吸附到最近的节点上 (实测 14:00 被改成 13:40, 保存成功、用户全程无感)。那个函数连同
  //   snapStartTimeToSlot() 已整体删除: 不做吸附, 就没得改, 用户填什么就是什么。
  //   代价是历史自定义时间的课在下拉里可能没有选中项 (label 显示「请选择时间段」),
  //   那是**如实展示**, 比悄悄改掉用户填的值好; 用户手动选一次即可。
  loadTimeSchedule() {
    if (!this.activeSchoolId) return;
    // v0.17.61 — 「还没问出结论」与「问过了, 就是没有」必须分开。
    //   改前 `this.scheduleNodes ? A : B` 用 scheduleNodes 同时表示这两件事, 于是打开弹窗的
    //   那一瞬 (fetch 还在途, scheduleNodes 还是 null) 先闪一句「该校区未配置课表」——
    //   请求慢一点就是一次**误报**, 用户会以为自己的校区真没配。
    //
    // v0.17.72 — 这里本来是**与 CalendarTab 各自一份**的同款谎言 (两个文件各写一遍,
    //   不是共用一条链): catch 里 scheduleNodes=null + scheduleResolved=true ⇒ 两处
    //   timeSlotDropdown / trialTimeSlotDropdown 都落到 scheduleMissingCompact()。
    //   学员从日历点「去约课」会撞上**同一个谎** —— 所以两处必须一起改, 只修日历是修一半。
    //   ⚠️ 本方法与 CalendarTab.scheduleState() 的四态口径保持一致, 两边不能各判各的。
    this.scheduleResolved = false;
    this.scheduleFailed = false;
    Api.getCurrentSchedule(this.activeSchoolId).then(res => {
      // ⚠️ 只有 reason === null 才是「有课表」。not_configured / stale 都当成没有,
      //   走占位提示 —— 与 CalendarTab.scheduleState() 同一个口径, 两边不能各判各的。
      if (res && res.reason === null && res.data && Array.isArray(res.data.nodes)) {
        const nodes = res.data.nodes.slice().sort((a, b) => Number(a.sort_order) - Number(b.sort_order) || Number(a.id) - Number(b.id));
        this.scheduleNodes = nodes.length ? nodes : null;
      } else {
        this.scheduleNodes = null;
      }
      // v0.17.64 — 课表到位后立刻把节点 id 补进草稿 (预填的时间是哪一档, 存的节点就得是哪一档)
      this.backfillScheduleNodeIds();
      this.scheduleResolved = true;
      m.redraw();
    }).catch(() => {
      // ⚠️ v0.17.72 — 失败**不是**「没配」。scheduleFailed 让两个 dropdown 改说自己的话。
      this.scheduleNodes = null;
      this.scheduleFailed = true;
      this.scheduleResolved = true;
      m.redraw();
    });
  }

  /**
   * v0.17.64 — **新建**模式: 按预填的 startTime 把课表节点 id 补进两份草稿。
   *
   * ## 为什么必须有这一步 (辉哥 10/16 线上现象的根因)
   *   改前新建课一打开, `form.scheduleNodeId` 恒为 null, 只有用户**手动点下拉**才会被赋值。
   *   而下拉那一栏的**显示**是靠 `slotByStartTime(startTime)` 从时间**反查**出来的 ——
   *   于是界面「看着选中了 10:15 – 11:00」, 实际 form 里节点 id 是空的。落库时 payload 也就带
   *   `schedule_node_id: null`, 后端只能拿 start_at 自己反推归属。
   *
   * ## 反推为什么一定会认错 (重叠档是允许的, v0.17.62 起)
   *   后端 `ScheduleResolver::nodeIn()` 与前端 `scheduleIndexOf()` 的口径都是
   *   「sort_order **第一条**命中的节点」。课表 10:15-11:00 与 10:30-11:15 重叠时,
   *   一节 10:30 的课同时落在两段里 → 两条路都判给**第一档 (10:15)**。
   *   结果: 用户点的是「10:30 – 11:15」那一行, 课存成 10:30, 却画在 **10:15 那一行**,
   *   而且那一行还显示已被占用、连第二节课都建不了 —— 界面与数据从此对不上。
   *
   * ## 为什么放在这里 (而不是 oninit 里同步做)
   *   `loadTimeSchedule()` 是异步的, oninit 那一刻 `this.scheduleNodes` 还是 null, 反查无从谈起。
   *   课表回来是**唯一**能确定「startTime 属于哪一档」的时机, 所以补在这一步。
   *
   * ## 三重保护 (任一不满足就**不写**, 宁可留 null 让后端按老路反推, 也不写错节点)
   *   ① edit 模式不碰 —— 回显老课的节点 id 是 v0.17.62 已有的语义, 不归本轮管;
   *   ② 调用方明确递了 prefillNodeId 且它确实属于当前这份课表 → 用它 (比反查准, 见 oninit 注释);
   *   ③ 否则按 startTime 反查, 命中 sort_order 第一条 —— 与 computeTimeSlots() 的下拉顺序同口径。
   */
  backfillScheduleNodeIds() {
    if (this.mode === 'edit') return;
    const nodes = Array.isArray(this.scheduleNodes) ? this.scheduleNodes : null;
    if (!nodes || !nodes.length) return;
    ['normal', 'trial'].forEach(key => {
      const f = this.drafts[key];
      if (!f || f.scheduleNodeId != null) return; // 用户已经选过 / 老数据回显, 一律不动

      if (this.prefillNodeId != null) {
        const hit = nodes.find(n => n && +n.id === this.prefillNodeId);
        if (hit) {
          f.scheduleNodeId = this.prefillNodeId;
          return;
        }
        // prefillNodeId 不属于当前这份课表 (课表刚被改过) —— 静默退回反查, 不抛不猜
      }

      // 与 computeTimeSlots() 同序 (sort_order, id), 反查命中「第一条 start_time 相同」的档
      const slot = this.computeTimeSlots().find(s => s.startTime === f.startTime);
      f.scheduleNodeId = slot && slot.nodeId != null ? +slot.nodeId : null;
    });
  }

  /**
   * 课表里的时间段清单 → 下拉选项。
   *
   * v0.17.57 — 数据源从「按 lesson_duration + break_duration 逐格推算」换成「课表节点」。
   *   改前那条算式在本地会产出 12 个 60 分钟的碎段 (09:00-10:00, 10:10-11:10, …),
   *   而后端 time-schedule 端点 v0.17.55 已下线 ⇒ it 恒返 [], 下拉永远是占位符。
   *   现在读的就是课表那几段, 一屏看完。
   *
   * ⚠️ 每项的 shape: `{ nodeId, startTime, endTime, label }`
   *   timeSlotDropdown / trialTimeSlotDropdown / slotByStartTime / onTimeSlotSelect
   *   全都按这几个字段读。
   *   ⚠️ **v0.17.62 起 shape 扩了 `nodeId`** —— 上一轮 (换数据源) 写的是
   *     「保持 { startTime, endTime, label } 不变」, 那条约束**本轮已作废**,
   *     别再拿它当理由把 nodeId 删掉。
   *   为什么必须带上 nodeId: 时间段**允许重叠** (辉哥 10/16: 「时间重叠是允许的」) 之后,
   *   同一个 start_at 可能同时落在好几段里 (10:45 ∈ 10:15-11:00 也 ∈ 10:30-11:15),
   *   只看 startTime 后端根本分不出用户选了哪个 —— 「用户选哪个就被排在哪个节点上」
   *   这句话要兑现, 就得把用户的选择**显式**一路带到后端落库。
   *
   * label 用 'HH:MM – HH:MM' (两端各 5 字符, 轴宽 41px 内放得下, 跟日历左列同一口径)。
   * ⚠️ 辉哥明确说过**不要**在选项后面加「模板时长 30 分」这种小字 —— 那是上一版的遗留诉求,
   *   本版已废, 谁想加回来先问一声。
   */
  computeTimeSlots() {
    if (!Array.isArray(this.scheduleNodes) || this.scheduleNodes.length === 0) return [];
    return this.scheduleNodes.map(n => {
      const startTime = String(n.start_time).slice(0, 5);
      const endTime = String(n.end_time).slice(0, 5);
      return {
        // v0.17.62 — 节点主键。**不可省**: 重叠区里它是「用户选了哪个」的唯一载体。
        nodeId: n.id != null ? +n.id : null,
        startTime: startTime,
        endTime: endTime,
        label: `${startTime} – ${endTime}`
      };
    });
  }

  // v0.7.1 — 选 dropdown 节点 → 同步 startTime, endTime 由卡时长算出
  // v0.17.62 — 同时记下 scheduleNodeId (用户选的就是它, 不能靠 startTime 反推)。
  onTimeSlotSelect(slot) {
    this.form.startTime = slot.startTime;
    this.form.scheduleNodeId = slot.nodeId != null ? +slot.nodeId : null;
    this.recalcEndFromCard();
    m.redraw();
  }

  /** 'HH:MM' → 当天零点起的分钟数 (跨天取模) */
  hhmmToMinutes(hhmm) {
    const p = String(hhmm == null || hhmm === '' ? '0:0' : hhmm).split(':').map(Number);
    return (p[0] || 0) * 60 + (p[1] || 0);
  }

  /** 分钟数 → 'HH:MM' (跨天取模) */
  minutesToHHMM(min) {
    const m = (Math.round(min) % 1440 + 1440) % 1440;
    return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
  }

  // ─── 学员卡联动 ────────────────────────────────────────────────

  // v0.6.7 — 选学员 → 清空卡 / 类型 / 分类 (联动清空)
  onStudentChange(student) {
    this.form.student = student;
    this.form.studentCard = null;
    this.form.lessonType = null;
    this.form.lessonCategory = null;
    if (student) {
      this.loadStudentCards(student);
    } else {
      this.studentCards = [];
    }
    m.redraw();
  }

  // v0.6.7 — 拉学员名下所有卡 (含 template)
  async loadStudentCards(student) {
    try {
      const cards = await Api.list('student-cards', {
        student_id: student.user_id || student.id,
        ...(this.activeSchoolId ? {
          school_id: this.activeSchoolId
        } : {})
      });
      this.studentCards = cards || [];
      // v0.6.8b — edit mode placeholder 升级: 如果 form.studentCard 是 id-only placeholder
      //   (oninit 时只设了 { id } 没 name), 用 find 从 cards 替换为完整 card
      //   让 studentCardDropdown label 用 template.name (而不是 '#id' fallback)
      // v0.8.8 — 升级条件放宽: 不再要求 `!this.form.studentCard.name`。
      //   改前只有 id-only 占位才升级, 这次 oninit 已经会用 lesson.student_card 完整对象
      //   (带 name, 但没有 student_id / school_id / template.lessons 等列表接口字段),
      //   旧条件会让它永远停在 lesson 的序列化快照上, 拿不到 student-cards 接口的更全数据。
      //   改后: 只要 this.studentCards 里有同 id 的卡就用接口那份 (更全), 找不到才保留原对象。
      if (this.form.studentCard && this.form.studentCard.id) {
        const full = this.studentCards.find(c => String(c.id) === String(this.form.studentCard.id));
        if (full) this.form.studentCard = full;
      }
      // v0.8.8 — 卡对象到位后再补跑一次 类型/分类 fallback:
      //   lesson.student_card 缺失时 (只有 student_card_id), resolveLessonType/Category 拿不到
      //   template, 只有等这一步把完整卡换上后才有 template 可读。
      //   只补 null 的 (lesson 表列已有值 / 首帧已 fallback 到值 → 都不动), 不覆盖已选值
      if (this.mode === 'edit') {
        if (!this.form.lessonType) {
          this.form.lessonType = this.resolveLessonType(this.lesson, this.form.studentCard);
        }
        if (!this.form.lessonCategory) {
          this.form.lessonCategory = this.resolveLessonCategory(this.lesson, this.form.studentCard);
        }
      }
      m.redraw();
    } catch (e) {
      console.error('[booking-modal] load student cards failed', e);
      this.studentCards = [];
      m.redraw();
    }
  }

  // v0.6.7 — 选学员卡 → 设默认课程类型 + 课程分类 (第一个) + 重算 end time
  // v0.6.36 — 课程分类默认仍取卡关联第一个, 但 dropdown 允许用户切换 (辉哥 9/29 23:26 反馈)
  onStudentCardChange(card) {
    this.form.studentCard = card;
    const tmpl = card && card.template;
    if (tmpl) {
      this.form.lessonType = tmpl.lesson_type_id || null;
      // 课程分类: 默认取卡关联第一个, 用户可在 dropdown 切换 (v0.6.36 改)
      const catIds = Array.isArray(tmpl.lesson_category_ids) ? tmpl.lesson_category_ids : [];
      this.form.lessonCategory = catIds.length > 0 ? catIds[0] : null;
    } else {
      this.form.lessonType = null;
      this.form.lessonCategory = null;
    }
    // v0.17.57 — 时长**由卡的 minutes 决定** (时间段是纯标记, 不约束课有多长),
    //   所以换卡就要重算结束时间: 30 分钟卡换成 90 分钟卡, 这节课就该从 30 分钟变 90 分钟。
    this.recalcEndFromCard();
    m.redraw();
  }

  // 时间段 dropdown — 选项 = 课表里的时间段清单 ('09:00 – 12:00' 之类), 单选。
  //   控件本体 (.ZS-nf-time / .ZS-form-input / Flarum Dropdown) 一行未改,
  //   只换了 computeTimeSlots() 的数据源。宽度、下拉交互、选中态全部沿用。
  //   本校区没有课表 (含 404 / not_configured / stale) → 返 placeholder div, 不白屏。
  timeSlotDropdown() {
    const slots = this.computeTimeSlots();
    if (slots.length === 0) {
      // v0.17.61 — 三态分流, 与课表日历的空态**同一个口径** (shared 组件 + shared 文案):
      //   还在加载 → 一句真实的加载态文案 (改前那句「加载课表时间段中...」原样保留)
      //   确认没有 → 提示块: 说明没配课表 + 「去「校区」配置课表」按钮 (可点)
      // ⚠️ 改前这里是 `this.scheduleNodes ? '该校区未配置课表' : '加载课表时间段中...'`,
      //   用 scheduleNodes 当「加载完了没有」的判据 —— 打开 modal 的那一瞬它必然还是 null,
      //   于是先闪「未配置课表」再跳成「加载中」。**顺序正好反了**, 而且慢网络下会停很久。
      // ⚠️ this.hide() 必须先跑: Modal 是挂在 body 上的全局浮层, 切 tab 不会把它带走,
      //   不关掉的话用户会以为按钮没反应 (后面挡住了「校区」tab)。
      if (!this.scheduleResolved) {
        return m("div", {
          className: "ZS-form-input"
        }, SCHEDULE_LOADING_TEXT);
      }
      // v0.17.72 — 失败说自己的话, 绝不借「没配课表」的嘴。
      //   ⚠️ 本条与 trialTimeSlotDropdown() **逐字相同** —— 这两处本来就是同一个 bug 的
      //   两份拷贝 (v0.17.61 已把文案收敛到 shared 组件, 这里跟着一起改, 别只改一处)。
      if (this.scheduleFailed) {
        return scheduleErrorCompact(() => this.loadTimeSchedule());
      }
      return scheduleMissingCompact(() => this.hide());
    }
    // 命中不到时**不合成自定义假项**, 也不**把用户填的时间改掉**。
    //   v0.17.57 之前这里靠 alignDraftsToSlots() 保证 startTime 一定落在某个节点上,
    //   那个函数会把 14:00 静默改成 13:40 —— 已连同 snapStartTimeToSlot() 删除。
    //   现在: 历史自定义时间的课在下拉里显示「请选择时间段」(如实展示, 用户自己选一次),
    //   form.startTime 保持原值不动。宁可多一次点击, 也不悄悄改用户填的值。
    const current = this.slotByStartTime(this.form.startTime);
    const label = current ? current.label : '请选择时间段';
    // slots.map 结果是一个 array (10 个 Button), Dropdown children = [array, conditional?]
    // mithril 渲染时 array 被当作 1 个 child (整体包到 <li> 里), 跟 conditional 兄弟 key 不一致会报错
    // 修复: 把所有兄弟 element 合并到 slots.map 的 callback 里, 共享 vnode.key 模式
    const items = [];
    slots.forEach(s => {
      items.push(m(Button
      // v0.17.62 — key 用 **nodeId**: 重叠节点下 label (即 start-end) 仍唯一,
      //   但 nodeId 才是稳定主键 (改时间段不必重挂组件)。nodeId 缺失时退回旧写法。
      , {
        key: s.nodeId != null ? `n${s.nodeId}` : `${s.startTime}-${s.endTime}`
        // v0.17.62 — 按 nodeId 判「当前选中」, 别再只比 startTime:
        //   重叠区里用户可能选了后一段 (startTime 不同, 但语义上是他选的那段)。
        ,
        active: !!current && (current.nodeId != null && s.nodeId != null && current.nodeId === s.nodeId || current.startTime === s.startTime),
        onclick: () => this.onTimeSlotSelect(s)
      }, s.label));
    });
    return (
      // v0.17.6 — 补 ZS-tf-menu: 让正课时间菜单跟体验课共用同一套限高。之前正课走
      // Flarum 原生 70vh、体验课走 .ZS-tf-menu 的 380px, 两个 tab 在同一屏行为相反。
      // ZS-followup-dropdown-menu 只带来 button[active] 高亮, 而正课 pane 根 div 不带
      // .ZS-followup-form (只有体验课 pane 带), 那条高亮规则命不中 ⇒ 零视觉副作用。
      m(Dropdown, {
        buttonClassName: "ZS-form-input",
        menuClassName: "ZS-followup-dropdown-menu ZS-tf-menu",
        label: label
      }, items)
    );
  }

  // ─── Dropdown helpers ──────────────────────────────────────

  roomDropdown() {
    const current = this.form.room;
    const label = current ? current.name : '请选择教室';
    if (this.rooms.length === 0) {
      return m("div", {
        className: "ZS-form-input"
      }, label);
    }
    return m(Dropdown, {
      buttonClassName: "ZS-form-input",
      label: label
    }, this.rooms.map(r => m(Button, {
      key: r.id,
      active: current && current.id === r.id,
      onclick: () => {
        this.form.room = r;
        m.redraw();
      }
    }, r.name)));
  }

  // v0.17.12 — 正课 tab 的教练已改用 normalPicker.render('normal_coach_id'),
  //   本方法**只剩体验课 tab (renderTrialPane) 一个调用点**, 别当成死代码删掉。
  coachDropdown() {
    const current = this.form.coach;
    const label = current ? current.display_name || current.username : '请选择教练';
    if (this.coaches.length === 0) {
      return m("div", {
        className: "ZS-form-input"
      }, label);
    }
    return m(Dropdown, {
      buttonClassName: "ZS-form-input",
      label: label
    }, this.coaches.map(c => m(Button, {
      key: c.id,
      active: current && current.id === c.id,
      onclick: () => {
        this.form.coach = c;
        m.redraw();
      }
    }, c.display_name || c.username)));
  }
  studentCardDropdown() {
    const student = this.form.student;
    const current = this.form.studentCard;
    if (!student) {
      return m("div", {
        className: "ZS-form-input"
      }, "\u8BF7\u5148\u9009\u62E9\u5B66\u5458");
    }
    if (this.studentCards.length === 0) {
      return m("div", {
        className: "ZS-form-input"
      }, "\u8BE5\u5B66\u5458\u6682\u65E0\u5B66\u5458\u5361");
    }
    const label = current ? current.name || current.template_name || `#${current.id}` : '请选择学员卡';
    return m(Dropdown, {
      buttonClassName: "ZS-form-input",
      label: label
    }, this.studentCards.map(c => m(Button, {
      key: c.id,
      active: current && current.id === c.id,
      onclick: () => this.onStudentCardChange(c)
    }, c.name || c.template_name || `#${c.id}`)));
  }

  // v0.6.7 — 课程类型 dropdown (single-select, filtered by 卡关联 1 项)
  //   卡关联 1 项 → 显示该项 (auto selected) + locked
  //   卡未配置 (NULL) → placeholder "未配置" disabled
  lessonTypeDropdown() {
    const current = this.form.lessonType;
    const card = this.form.studentCard;
    if (!card) {
      return m("div", {
        className: "ZS-form-input"
      }, "\u8BF7\u5148\u9009\u62E9\u5B66\u5458\u5361");
    }
    // 单项 dropdown (filtered by 卡关联 1 项)
    if (!current) {
      return m("div", {
        className: "ZS-form-input"
      }, "\u672A\u914D\u7F6E");
    }
    const label = LessonOptions.labelFor('type', current);
    return m(Dropdown, {
      buttonClassName: "ZS-form-input",
      label: label
    }, m(Button, {
      active: true
    }, label, m("span", {
      className: "Badge--builtin"
    }, "\u5361\u5173\u8054")));
  }

  // v0.6.7 — 课程分类 dropdown (single-select, 取卡关联**第一个**)
  //   卡关联 N 项 → 取第一个 auto 选中 + locked
  //   卡未配置 (空数组) → placeholder "未配置" disabled
  // v0.6.36 — 课程分类 dropdown 显示卡关联的所有 N 个分类, 允许用户切换 (辉哥 9/29 23:26 反馈).
  //   默认值仍是 onStudentCardChange 取第一个 (首次进 modal 默认行为不变),
  //   这里只负责渲染: 渲染 catIds 里所有项, active 标 form.lessonCategory.
  //   卡关联 N 项 → 渲染 N 个 Button, user 可切换 (移除原 hardcode "卡首个" badge)
  //   卡未配置 (空数组) → placeholder "未配置" disabled (跟 v0.6.7 一致)
  lessonCategoryDropdown() {
    const current = this.form.lessonCategory;
    const card = this.form.studentCard;
    if (!card) {
      return m("div", {
        className: "ZS-form-input"
      }, "\u8BF7\u5148\u9009\u62E9\u5B66\u5458\u5361");
    }
    if (!current) {
      return m("div", {
        className: "ZS-form-input"
      }, "\u672A\u914D\u7F6E");
    }
    const tmpl = card.template || {};
    const catIds = Array.isArray(tmpl.lesson_category_ids) ? tmpl.lesson_category_ids.slice() : [];
    // v0.8.8 — 当前已选/已回显的分类不在卡 template 的关联列表里时 (历史约课存的是旧分类,
    //   或约课后管理员改过卡的关联分类), 把它补进选项, 否则 dropdown 会渲染成 0 项 ——
    //   按钮上虽然还显示着 label, 但点开是空菜单, 用户看不出自己选的是啥、能选啥
    if (current && !catIds.some(cid => String(cid) === String(current))) catIds.push(current);
    const label = LessonOptions.labelFor('category', current);
    return m(Dropdown, {
      buttonClassName: "ZS-form-input",
      label: label
    }, catIds.map(cid => {
      const optLabel = LessonOptions.labelFor('category', cid);
      return m(Button, {
        key: cid,
        active: String(cid) === String(current),
        onclick: () => {
          this.form.lessonCategory = cid;
          m.redraw();
        }
      }, optLabel);
    }));
  }

  // ══════════════════════════════════════════════════════════════════
  //  v0.17.0 — 体验课 tab (分区①客户信息 / ②体验卡 / ③排课信息)
  //
  //  ⚠️ 本区块**不得出现任何「在校生 / 在册客户」维度的字段或文案** ——
  //     体验课的客户还没注册成 Flarum 用户, 系统里查不到这个人。
  //     硬验收项 (辉哥口径): 体验课 pane 区间的禁用词命中数必须为 **0**,
  //     **含注释在内** —— 所以本注释刻意不把那个词原样写出来 (否则注释自己
  //     就把计数顶成 1, 验收脚本永远假失败)。验收命令见 CHANGELOG v0.17.0。
  //     写代码时需要指代那个人, 一律写「客户」。
  //
  //  ⚠️ JSX 事件一律小写 onclick。Flarum 2 打包产物里 setAttr 会做
  //     `addEventListener(r.slice(2), ...)`, r 就是属性名本身, 全 bundle 无
  //     任何 toLowerCase 归一化 —— 大写 onClick 会变成 addEventListener('Click'),
  //     而 DOM 事件名大小写敏感 ⇒ **永不触发且不报错**。仓库既有 184 处全是小写。
  // ══════════════════════════════════════════════════════════════════

  /**
   * 体验卡候选 = builtin-trial 且 lessons === 1。
   *
   * 用 `Number(t.lessons) === 1` 而不是 `t.lessons === 1`: 后端这一列是 integer,
   * 但 JSON 路径不同的时候可能是字符串 "1", 严格比较会**永远不成立** ⇒
   * 下拉恒空, 而界面上看不出任何异常 (典型的「以为加了功能其实没生效」)。
   */
  trialTemplates() {
    return (this.cardTemplates || []).filter(t => t && t.lesson_type_id === 'builtin-trial' && Number(t.lessons) === 1);
  }

  /**
   * 选体验卡模板 → 课程类型固定 builtin-trial + 课程分类带出模板的第一个 + 重算结束时间。
   *
   * 课程分类口径跟正课的 onStudentCardChange 一致 (取模板关联的**第一个**),
   * 用户可在下拉里改。
   */
  onTrialTemplateChange(tpl) {
    const f = this.drafts.trial;
    f.template = tpl;
    f.lessonType = 'builtin-trial'; // 体验课类型恒定, 只读展示
    const catIds = tpl && Array.isArray(tpl.lesson_category_ids) ? tpl.lesson_category_ids : [];
    f.lessonCategory = catIds.length > 0 ? catIds[0] : null;
    // v0.17.5 — 换体验卡不再改时长, 这里只是把结束时间对齐到区块尾
    this.recalcTrialEnd();
    m.redraw();
  }

  /**
   * 结束时间 = 开始时间 + **体验卡模板的 minutes** (跟正课 recalcEndFromCard 同一算法)。
   *
   * ⚠️ v0.17.57 — 这里在 v0.17.5 被改成了「结束时间 = 时间段的尾」, 本轮改回卡时长。
   *   理由同 recalcEndFromCard: 时间段是纯标记, 新规则下跨度是小时级的,
   *   拿它当「一节课的时长」会一选就排出 3~5 小时的课。
   *   体验卡只决定**核销几次 + 这节课多长**, 跟正课口径一致。
   *   跟正课分成两个方法的原因跟 v0.17.0 一样: 要操作的是 drafts.trial 而不是 this.form。
   */
  recalcTrialEnd() {
    const f = this.drafts.trial;
    const slot = this.slotByStartTime(f.startTime);
    let minutes = 0;
    if (f.template && +f.template.minutes > 0) {
      minutes = +f.template.minutes;
    } else if (slot) {
      minutes = this.hhmmToMinutes(slot.endTime) - this.hhmmToMinutes(slot.startTime);
    }
    if (!(minutes > 0)) return false;
    f.endTime = this.minutesToHHMM(this.hhmmToMinutes(f.startTime) + minutes);
    return true;
  }

  /** 体验卡下拉。未选时红字就地提示 (规格要求), 由 pane 渲染。 */
  trialTemplateDropdown() {
    const tpls = this.trialTemplates();
    const current = this.drafts.trial.template;
    if (tpls.length === 0) {
      return m("div", {
        className: "ZS-form-input ZS-tf-empty"
      }, "\u6682\u65E0 1 \u6B21\u8BFE\u65F6\u7684\u4F53\u9A8C\u5361\u6A21\u677F");
    }
    const label = current ? current.name : '请选择体验卡';
    const items = tpls.map(t => m(Button, {
      key: t.id,
      active: !!current && current.id === t.id,
      onclick: () => this.onTrialTemplateChange(t)
    }, t.name, m("span", {
      className: "ZS-tf-chip"
    }, "\u4F53\u9A8C\u8BFE"), m("span", {
      className: "ZS-tf-meta"
    }, Number(t.lessons), " \u6B21\u8BFE\u65F6 \xB7 ", Number(t.minutes) || 0, " \u5206\u949F")));
    return m(Dropdown, {
      className: "ZS-followup-dropdown"
      // v0.17.6 — 换回 .ZS-form-input: 之前沿用「新建客户跟进」的 filter 皮肤
      // (16px 圆角 / 10px 14px / 42px 高), 跟同一 modal 正课 tab 的 36px 表单控件
      // 长得不一样。宽度不丢: .ZS-BookingModal .Dropdown-toggle (LESS L4865) 自带
      // width:100%, .ZS-form-input (LESS L4777) 同款。
      ,
      buttonClassName: "ZS-form-input",
      menuClassName: "ZS-followup-dropdown-menu ZS-tf-menu",
      label: label,
      lazyDraw: true
    }, items);
  }

  /**
   * 通用「把 Flarum 原生 Dropdown 当表单控件用」的渲染。
   * 照抄 SalesFollowupCreateModal.renderChoiceDropdown —— 包括它注释里记的
   * 「children 必须是**一个**数组 vnode」那条 (写成两个子节点会让 Dropdown
   * 只包出一个 <li>, core CSS 是直接子选择器, 样式会全废)。
   */
  trialChoiceDropdown(opts) {
    const f = this.drafts.trial;
    const current = (opts.options || []).find(o => o.value === String(opts.value));
    const items = (opts.options || []).length ? opts.options.map(o => m(Button, {
      key: o.value,
      active: !!current && current.value === o.value,
      onclick: () => {
        f[opts.formKey] = o.value;
        m.redraw();
      }
    }, o.label)) : [m(Button, {
      key: "__empty",
      disabled: true
    }, "\u6682\u65E0\u53EF\u9009\u9879")];
    return m(Dropdown, {
      className: "ZS-followup-dropdown",
      buttonClassName: "ZS-form-input",
      menuClassName: "ZS-followup-dropdown-menu",
      icon: opts.icon,
      label: current ? current.label : opts.placeholder,
      lazyDraw: true
    }, items);
  }

  /** 分区卡片外壳 (.ZS-section 三件套, 跟新建跟进 modal 同款)
   *  v0.17.8 — 正课 tab 也改用分区卡片, 两个 tab 共用这一个壳 (原先只服务体验课)
   *  ⚠️ v0.19.20 (批 41 · 段②) — 实现**搬**进共用入口 `utils/FormPrimitives.js`
   *    (改前 `renderSection` / `fieldIcon` 在本文件里各抄了一份 ⇒ 本仓铁律「一个逻辑一个入口」)
   *    ⇒ 本文件的 DOM 结构与样式**一字不动**。 */

  // ─── v0.18.86 (P2-4) — 「该客户已有体验跟进」提示 ──────────────────────────
  //
  // ## 现象 (P2-4)
  //   体验课按「同校区 + 同手机号 + is_trial=1」归并跟进 (后端 CreateTrialLessonController)。
  //   销售先手建了一条跟进 (is_trial=0), 再约体验课 ⇒ 体验课**另建一条** ⇒
  //   销售跟进列表里两条同名同号, 只靠一枚「体验」徽标区分 ⇒
  //   转学员时选错那条 ⇒ 3 张体验卡**一张都绑不上**, 而界面上**没有任何提示**。
  //
  // ## 本提示只**告知**, 不代替销售做决定
  //   ⛔ **不自动**帮销售切到那条记录 (销售可能故意要新建一条);
  //   ⛔ **不改**弹层的既有流程 (照旧提交、照旧归并);
  //   ⇒ 它只是一句「这次会沿用哪条」的提示。
  //
  // ## 数据从哪来 (⛔ 不新开接口)
  //   `GET /sales-followups/check-phone?phone=…&school_id=…` (既有只读端点),
  //   v0.18.86 给它**加了两个最小字段**: `trial_followup_count` / `trial_card_count`。
  //   ⇒ 卡数是**后端数出来的真实值**, ⛔ 前端不许写死数字。
  //
  // ## 防抖 + 序号作废 + 失败中性 (同 SalesFollowupCreateModal 的三态纪律)
  //   · 防抖 300ms: 失焦常常紧跟着「粘贴完就点下一个框」, 不防抖就是两次请求;
  //   · 序号作废: 输入一改, 在途请求的结论立刻失效 (否则慢响应回来覆盖新输入的提示);
  //   · 失败中性: 请求失败**不能**当成「没有体验跟进」(那是给销售一个假的安全信号),
  //     ⛔ 也不许拦提交 (真校验在服务端)。
  onTrialPhoneInput(e) {
    this.drafts.trial.phone = e.target.value;
    this._trialFollowupSeq += 1;
    if (this._trialFollowupTimer) {
      clearTimeout(this._trialFollowupTimer);
      this._trialFollowupTimer = null;
    }
    if (this.trialFollowupCheck.state !== 'idle') {
      this.setTrialFollowupCheck('idle', 0, 0);
    }
    // 失焦后再查 (同款 300ms 防抖)
    this._trialFollowupTimer = setTimeout(() => {
      this._trialFollowupTimer = null;
      this.checkTrialFollowup();
    }, 300);
  }
  setTrialFollowupCheck(state, followupCount, cardCount) {
    this.trialFollowupCheck = {
      state,
      followupCount: followupCount || 0,
      cardCount: cardCount || 0
    };
    m.redraw();
  }
  async checkTrialFollowup() {
    const phone = String(this.drafts.trial.phone || '').trim();
    const schoolId = +this.activeSchoolId;
    const seq = ++this._trialFollowupSeq;

    // 校区没选 / 电话格式还没对 ⇒ 不查 (这里**不**冒充「没有体验跟进」)
    if (!schoolId || !/^1[3-9]\d{9}$/.test(phone)) {
      this.setTrialFollowupCheck('idle', 0, 0);
      return;
    }
    this.setTrialFollowupCheck('checking', 0, 0);
    try {
      const r = await Api.get('sales-followups/check-phone', {
        phone,
        school_id: schoolId
      });
      if (seq !== this._trialFollowupSeq) return; // 旧请求后到 → 丢弃
      if (!r || r.valid !== true) {
        this.setTrialFollowupCheck('idle', 0, 0);
        return;
      }
      const fc = Number(r.trial_followup_count) || 0;
      if (fc > 0) {
        this.setTrialFollowupCheck('has', fc, Number(r.trial_card_count) || 0);
      } else {
        this.setTrialFollowupCheck('idle', 0, 0);
      }
    } catch (e) {
      if (seq !== this._trialFollowupSeq) return;
      console.warn('[booking-trial] trial followup check failed', e);
      // ⚠️ 查失败 = 中性: ⛔ 不渲染任何结论, ⛔ 不拦提交
      this.setTrialFollowupCheck('unknown', 0, 0);
    }
  }

  /**
   * 体验跟进提示条 (P2-4)。
   *
   * ⚠️ 文案里的**卡数是后端数出来的真实值** (⛔ 不许写死数字)。
   * ⚠️ ⛔ 文案里不许出现「待拍板 / 见 SXX / 策略待确认」这类给开发看的字样。
   */
  renderTrialFollowupHint() {
    const st = this.trialFollowupCheck.state;
    if (st !== 'has') return null;
    const cards = this.trialFollowupCheck.cardCount;
    return m("div", {
      className: "ZS-tf-hint ZS-tf-hint-trial"
    }, m("i", {
      className: "fas fa-circle-info"
    }), `该客户已有体验跟进记录，本次将沿用，${cards} 张体验卡已挂在那条记录下。`);
  }
  renderTrialPane() {
    const f = this.drafts.trial;
    const slots = this.computeTimeSlots();
    const tpls = this.trialTemplates();

    // v0.17.5 — 删掉了这里的 showCustomEndHint / nodeEndTime: 结束时间恒等于区块尾,
    //   「模板时长 ≠ 排版单节时长」这件事已经不存在了, 提示无从触发。

    const catOptions = (this.categories || []).filter(c => c.enabled).map(c => ({
      value: String(c.id),
      label: c.name
    }));
    const chOptions = (this.channels || []).filter(c => c.enabled).map(c => ({
      value: String(c.id),
      label: c.name
    }));
    return m("div", null, m("div", {
      className: "ZS-tf-banner"
    }, m("i", {
      className: "fas fa-circle-info"
    }), "\u63D0\u4EA4\u540E\u5C06\u5199\u5165\u8BFE\u8868\uFF0C\u5E76\u81EA\u52A8\u751F\u6210\u4E00\u6761\u9500\u552E\u8DDF\u8FDB\u8BB0\u5F55\uFF0C\u8DDF\u8FDB\u9636\u6BB5\u7F6E\u4E3A\u300C\u5DF2\u7EA6\u8BFE\u300D\u3002"), m("div", {
      className: "ZS-modal-form ZS-followup-form ZS-trial-form"
    }, renderSection(1, '客户信息', '这条记录「关于谁」', [m("div", {
      className: "ZS-form-row full"
    }, m("div", {
      className: "ZS-form-group ZS-tf-name"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-user'), " \u59D3\u540D ", m("span", {
      className: "req"
    }, "*")), m("input", {
      className: "ZS-form-input",
      placeholder: "\u4F8B: \u9648\u5C0F\u96E8",
      value: f.customer_name,
      oninput: e => {
        f.customer_name = e.target.value;
      }
    }))), m("div", {
      className: "ZS-form-row"
    }, m("div", {
      className: "ZS-form-group ZS-tf-age"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-calendar-day'), " \u5E74\u9F84"), m("input", {
      className: "ZS-form-input",
      type: "number",
      min: "0",
      max: "120",
      step: "0.1",
      placeholder: "\u4F8B: 34 \u6216 7.5",
      value: f.age
      // ⚠️ 原样存字符串, 不在这里 +value: 空串必须一路保持空串
      ,
      oninput: e => {
        f.age = e.target.value;
      }
    })), m("div", {
      className: "ZS-form-group ZS-tf-phone"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-phone'), " \u7535\u8BDD ", m("span", {
      className: "req"
    }, "*")), m("input", {
      className: "ZS-form-input",
      placeholder: "\u4F8B: 13800138000",
      value: f.phone
      // v0.18.86 (P2-4) — 失焦后查「该客户是否已有体验跟进」,
      //   有就在下面给一条明确提示 (⛔ 只告知, 不自动切记录)。
      ,
      oninput: e => {
        this.onTrialPhoneInput(e);
      },
      onblur: () => {
        this.checkTrialFollowup();
      }
    }), this.renderTrialFollowupHint())), m("div", {
      className: "ZS-form-row"
    }, m("div", {
      className: "ZS-form-group ZS-tf-chan"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-bullhorn'), " \u6765\u6E90\u6E20\u9053"), this.trialChoiceDropdown({
      icon: 'fas fa-bullhorn',
      options: chOptions,
      value: f.lead_channel_id,
      placeholder: '— 选择来源渠道 —',
      formKey: 'lead_channel_id'
    })), m("div", {
      className: "ZS-form-group ZS-tf-cat"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-tag'), " \u8BFE\u7A0B\u5206\u7C7B"), this.trialChoiceDropdown({
      icon: 'fas fa-tag',
      options: catOptions,
      value: f.lesson_category_id,
      placeholder: '— 选择课程分类 —',
      formKey: 'lesson_category_id'
    }))), m("div", {
      className: "ZS-form-row full"
    }, this.trialPicker.render('sales_id')), m("div", {
      className: "ZS-form-row full"
    }, m("div", {
      className: "ZS-form-group ZS-tf-note"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-file-lines'), " \u4E2A\u4EBA\u60C5\u51B5", m("span", {
      className: "hint"
    }, "\u5BA2\u6237\u80CC\u666F / \u8EAB\u4F53\u72B6\u51B5 / \u7279\u6B8A\u8981\u6C42")), m("textarea", {
      className: "ZS-form-input",
      rows: "2",
      placeholder: "\u4F8B: \u6210\u4EBA, \u96F6\u57FA\u7840\u6ED1\u51B0, \u6015\u51B7, \u819D\u76D6\u6709\u65E7\u4F24",
      value: f.personal_note,
      oninput: e => {
        f.personal_note = e.target.value;
      }
    })))]), renderSection(2, '体验卡', '选卡模板 · 可反复使用', [m("div", {
      className: "ZS-form-row full"
    }, m("div", {
      className: "ZS-form-group ZS-tf-card"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-id-card'), " \u4F53\u9A8C\u5361 ", m("span", {
      className: "req"
    }, "*")), this.trialTemplateDropdown(), !f.template && m("div", {
      className: "ZS-tf-hint danger"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), tpls.length === 0 ? '没有可选的体验卡模板，请先在「卡种模板」里建一个「体验」类型且课时数为 1 的模板' : '请选择体验卡后再保存'), f.template && m("div", {
      className: "ZS-tf-hint"
    }, "\u5C06\u6838\u9500 1 \u6B21 \xB7 \u63D0\u4EA4\u540E\u4E3A\u8BE5\u5BA2\u6237\u65B0\u5EFA\u4E00\u5F20\u4F53\u9A8C\u5361\u5E76\u7ACB\u5373\u4F7F\u7528")))]), renderSection(3, '排课信息', '与正课一致', [m("div", {
      className: "ZS-form-row ZS-tf-2col"
    }, m("div", {
      className: "ZS-form-group ZS-tf-room"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-door-open'), " \u6559\u5BA4 ", m("span", {
      className: "req"
    }, "*")), this.roomDropdown()), m("div", {
      className: "ZS-form-group ZS-tf-coach"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-user-tie'), " \u6559\u7EC3 ", m("span", {
      className: "req"
    }, "*")), this.coachDropdown())), m("div", {
      className: "ZS-form-row full"
    }, m("div", {
      className: "ZS-form-group ZS-tf-time"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-clock'), " \u65F6\u95F4 ", m("span", {
      className: "req"
    }, "*")), m("div", {
      className: "ZS-time-row"
    }, this.renderDateField('trial'), this.trialTimeSlotDropdown(slots))))])), this.error && m("div", {
      className: "ZS-tf-hint danger",
      style: "margin:0 14px 8px;"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), " ", this.error), m("div", {
      style: "margin:0 14px 8px;"
    }, this.liveConflictNotice()), m("div", {
      className: "ZS-tf-footnote"
    }, "\u4FDD\u5B58\u540E\u5199\u5165\u8BFE\u8868\uFF0C\u5E76\u5728\u9500\u552E\u8DDF\u8FDB\u65B0\u5EFA\u300C\u5DF2\u7EA6\u8BFE\u300D\u8BB0\u5F55\uFF0C\u540C\u65F6\u6838\u9500\u4F53\u9A8C\u5361 1 \u6B21"));
  }

  /**
   * 时间节点下拉。
   *
   * 复用既有的 computeTimeSlots() (校区课表的 schedule_nodes 清单),
   * 跟正课 timeSlotDropdown() **同款数据源、同套规则**, 只是多一个「共 N 个可选节点」的头。
   * v0.17.57 — 命中不到节点时不合成 "(自定义)" 假项, 当前值只按 f.startTime 反算;
   *   也**不再把历史自定义时间预先吸附**到最近节点 (那是静默改用户数据的来源)。
   */
  trialTimeSlotDropdown(slots) {
    if (!slots || slots.length === 0) {
      // v0.17.61 — 与正课 timeSlotDropdown() **完全同一个口径**。
      //   改前这两处各自抄了一份 `this.scheduleNodes ? ... : ...`, 是同一个 bug 的两份拷贝:
      //   加载中先闪「未配置课表」, 而且没有出口。现在共用 shared 组件, 改一处即两处。
      if (!this.scheduleResolved) {
        return m("div", {
          className: "ZS-form-input"
        }, SCHEDULE_LOADING_TEXT);
      }
      // v0.17.72 — 失败说自己的话, 绝不借「没配课表」的嘴。
      //   ⚠️ 本条与 trialTimeSlotDropdown() **逐字相同** —— 这两处本来就是同一个 bug 的
      //   两份拷贝 (v0.17.61 已把文案收敛到 shared 组件, 这里跟着一起改, 别只改一处)。
      if (this.scheduleFailed) {
        return scheduleErrorCompact(() => this.loadTimeSchedule());
      }
      return scheduleMissingCompact(() => this.hide());
    }
    const f = this.drafts.trial;
    // v0.17.62 — 与正课同一个口径: 优先按 scheduleNodeId 找当前项, 找不到才按 startTime。
    const current = (f.scheduleNodeId != null ? slots.find(s => s.nodeId != null && s.nodeId === f.scheduleNodeId) : null) || slots.find(s => s.startTime === f.startTime) || null;
    const label = current ? current.label : '请选择时间段';
    const items = slots.map(s => m(Button, {
      key: s.nodeId != null ? `n${s.nodeId}` : `${s.startTime}-${s.endTime}`,
      active: !!current && (current.nodeId != null && s.nodeId != null && current.nodeId === s.nodeId || current.startTime === s.startTime),
      onclick: () => {
        f.startTime = s.startTime;
        f.endTime = s.endTime;
        // v0.17.62 — 同正课: 记住用户选的是哪个节点 (体验课一样要占课槽)。
        f.scheduleNodeId = s.nodeId != null ? +s.nodeId : null;
        m.redraw();
      }
    }, s.label));
    // 下拉头: 来源说明 + 节点总数 (设计稿要求)
    //   ⚠️ 用**禁用 Button** 而不是裸 <li>: Flarum 的 listItems() 会把每个非
    //     isListItem 的子节点再包一层 <li>, 裸 <li> 会被套成 <li><li>…</li></li>,
    //     结构就废了。Button 走的是既有通路, 不碰 Dropdown 内部实现。
    items.unshift(m(Button, {
      key: "__head",
      className: "ZS-tf-drop-head",
      disabled: true
    }, m("span", null, "\u6765\u81EA\u6821\u533A\u65F6\u95F4\u6392\u7248"), m("span", {
      className: "n"
    }, "\u5171 ", slots.length, " \u4E2A\u53EF\u9009\u8282\u70B9")));
    return m(Dropdown, {
      className: "ZS-followup-dropdown",
      buttonClassName: "ZS-form-input",
      menuClassName: "ZS-followup-dropdown-menu ZS-tf-menu",
      label: label,
      lazyDraw: true
    }, items);
  }

  // ─── Validate + Submit ────────────────────────────────────────

  /**
   * v0.18.72 — 起点必须落在当天课表的某个时间段内 (**前端提前拦**, 别等后端 422)
   *
   * ## 根因 (为什么以前会漏)
   *   用户不选「时间段」时, startTime = 日期框里的**当前时刻** (实测 08:28) ⇒
   *   提交后被后端 ScheduleResolver::assertResolves 422 掉 ⇒ 建出**消不掉的僵尸课**。
   *   本方法让用户**当场**就看到为什么不行, 不用等后端回一条 422。
   *
   * ## 口径 (与后端逐字一致)
   *   后端: ScheduleNode::containsMinuteOfDay() = `minute >= start && minute <= end` (**闭区间**)
   *         ScheduleResolver::nodeIn() = 遍历命中即 return (重叠档取排在前面的那一档)。
   *   前端: 复用本文件**既有**的 computeTimeSlots() + hhmmToMinutes(),
   *         ⛔ 不另拉一份数据、⛔ 不另写一套判定 (两处各写一遍 = 迟早分叉)。
   *
   * ## 两条生效边界 (少一条就会误伤)
   *   ① **create 模式才拦**: edit 模式要回显**历史自定义时间**的老课
   *      (startTime 本来就不落在任何时间段上, 见 oninit L251-252 注释) ⇒ 不许把老课拦死。
   *   ② **该校区配了课表才拦**: 没配课表时后端 assertResolves 是彻底的 no-op
   *      (第一件事就是查该校区有没有版本, 见 CreateLessonController L124 注释),
   *      前端也不许拦 ⇒ 与后端同一条底线。
   *
   * @param {string} startTime 'HH:MM'
   * @returns {string|null} 错误文案 (null = 放行)
   */
  assertTimeInSchedule(startTime) {
    // 边界①
    if (this.mode === 'edit') return null;
    // 边界②
    const slots = this.computeTimeSlots();
    if (!slots || slots.length === 0) return null;
    const m = this.hhmmToMinutes(startTime);
    const ok = slots.some(s => m >= this.hhmmToMinutes(s.startTime) && m <= this.hhmmToMinutes(s.endTime));
    if (ok) return null;

    // ⚠️ 前半句与后端 ScheduleResolver::MSG_NOT_IN_NODE ('该时间不在课表的时间段内') **同源**
    //   —— 后端那句在 src/Service/ScheduleResolver.php:83, 两边不许各写各的。
    return `该时间不在课表的时间段内（当前 ${startTime}），请从「时间段」下拉中选择`;
  }

  /**
   * v0.17.0 — 体验课提交前校验。
   *
   * 口径跟 SalesFollowupCreateModal.validate() 对齐 (同一个业务表单的两种入口):
   * 年龄用 1e-9 容差判「最多 1 位小数」, 电话 11 位大陆号, 经办人必填。
   * 空串保持空串, 归一化只在 buildTrialPayload 里发生一次。
   */
  trialValidate() {
    const f = this.drafts.trial;
    if (!String(f.customer_name).trim()) return '请填写姓名';
    if (String(f.customer_name).trim().length > 64) return '客户姓名过长 (最多 64 个字)';
    const age = String(f.age).trim();
    if (age !== '') {
      const a = Number(age);
      if (!isFinite(a)) return '年龄必须是数字';
      if (Math.abs(a - Math.round(a * 10) / 10) > 1e-9) return '年龄最多支持 1 位小数';
      if (a < 0 || a > 120) return '年龄需在 0-120 之间';
    }
    const phone = String(f.phone).trim();
    if (phone === '') return '电话必填';
    if (!/^1[3-9]\d{9}$/.test(phone)) return '电话格式不正确 (需 11 位大陆手机号)';
    if (!f.sales_id) return '请选择经办人';
    // 体验卡必选 (方案 A 口径: 必选, 否则账目不闭合)
    if (!f.template) return '请选择体验卡';
    if (!f.room) return '请选择教室';
    if (!f.coach) return '请选择教练';
    if (!f.startTime || !f.endTime) return '请选择时间段';
    if (!this.activeSchoolId) return '请先选择校区';
    // v0.18.72 — 起点必须落在课表时间段内 (与正课共用 assertTimeInSchedule, ⛔ 不各写一遍)
    const timeErr = this.assertTimeInSchedule(f.startTime);
    if (timeErr) return timeErr;
    return null;
  }

  /**
   * ⚠️ 全文件**唯一**允许做「字符串 → 类型」转换的地方 (同 SalesFollowupCreateModal
   *   v0.16.0 的口径): 输入阶段一律原样存字符串, 空串在这里才变 null。
   *   「空值是空值不是 0」—— `(int)''` 是 0, 图省事就会把「没填年龄」写成 0 岁。
   */
  buildTrialPayload() {
    const f = this.drafts.trial;
    const age = String(f.age).trim();
    const phone = String(f.phone).trim();
    return {
      school_id: +this.activeSchoolId,
      room_id: +f.room.id,
      // coach list 返 id, staff list 返 user_id (跟正课 payload 同款兼容)
      coach_id: +(f.coach.id || f.coach.user_id),
      customer_name: String(f.customer_name).trim(),
      age: age === '' ? null : age,
      phone: phone === '' ? null : phone,
      lead_channel_id: f.lead_channel_id || null,
      personal_note: String(f.personal_note).trim() || null,
      sales_id: +f.sales_id,
      // 课程分类两个来源, 取优先级高的那个:
      //   ① 分区①里用户**手动**选的 (f.lesson_category_id)
      //   ② 选卡模板时自动带出的**第一个** (f.lessonCategory, 见 onTrialTemplateChange)
      //   都没有 → null, 后端再回落模板关联的第一个。
      //   后端把这一列同时写进跟进记录和 lesson, 两边永远同源, 不会出现
      //   「跟进记着羽毛球、课块上写着网球」这种对不上的状态。
      lesson_category_id: f.lesson_category_id || f.lessonCategory || null,
      card_template_id: +f.template.id,
      start_at: `${f.date}T${f.startTime}:00`,
      end_at: `${f.date}T${f.endTime}:00`,
      // v0.17.62 — 体验课同正课: 带上用户选的那个节点 (理由与正课 payload 同一条)。
      schedule_node_id: f.scheduleNodeId != null ? +f.scheduleNodeId : null,
      notes: null
    };
  }
  async submitTrial() {
    const err = this.trialValidate();
    if (err) {
      this.error = err;
      m.redraw();
      return;
    }
    this.error = '';
    this.saving = true;
    m.redraw();
    try {
      const payload = this.buildTrialPayload();
      await Api.createTrialLesson(payload);
      this.saving = false;
      if (this.onCloseCb) this.onCloseCb();
      this.hide();
    } catch (e) {
      this.saving = false;
      const msg = errReason(e, '保存失败');
      this.error = msg;
      console.error('[booking-modal] trial save failed', e);
      m.redraw();
    }
  }

  /**
   * v0.12.0 — 「计费课时」二选一 (半节 / 一节)
   *
   * 辉哥明确要求「只有半节和一节两个选项」+「不要步进器」, 所以这里用两个
   * toggle 按钮而不是 <input type="number">: 步进器会让 0.3 / 0.75 这类
   * 「能填出来但后端一律 422」的值出现在界面上, 用户填完才知道错。
   * 两个按钮从 UI 层面就把取值空间锁死成 {0.5, 1}。
   *
   * 已消课的课锁定不可改 —— 跟后端 UpdateLessonController 的同一道保护对齐,
   * 免得用户在这里改了、最后被后端 422 兜回去 (界面上先说清楚比事后报错好)。
   */
  chargeToggle() {
    const locked = this.mode === 'edit' && this.lesson && this.lesson.status === 'completed';
    const opts = [{
      v: 1.0,
      label: '一节'
    }, {
      v: 0.5,
      label: '半节'
    }];
    return m("div", {
      className: 'zs-charge-toggle' + (locked ? ' is-locked' : '')
    }, opts.map(o => {
      const active = this.form.lessonCharge === o.v;
      return m("button", {
        key: String(o.v),
        type: "button",
        className: 'zs-charge-opt' + (active ? ' is-active' : ''),
        disabled: locked,
        onclick: () => {
          if (locked) return;
          this.form.lessonCharge = o.v;
          m.redraw();
        }
      }, o.label);
    }));
  }
  validate() {
    if (!this.form.room) return '请选择教室';
    if (!this.form.coach) return '请选择教练';
    if (!this.form.student) return '请选择学员';
    if (!this.form.studentCard) return '请选择学员卡';
    if (!this.form.lessonType) return '请先选择学员卡 (卡未设置课程类型)';
    if (!this.form.lessonCategory) return '请先选择学员卡 (卡未设置课程分类)';
    if (!this.form.startTime) return '请选择开始时间';
    if (!this.form.endTime) return '请选择结束时间';
    if (!this.activeSchoolId) return '请先选择校区';
    // v0.18.72 — 起点必须落在课表时间段内 (与体验课共用 assertTimeInSchedule, ⛔ 不各写一遍)
    const timeErr = this.assertTimeInSchedule(this.form.startTime);
    if (timeErr) return timeErr;
    return null;
  }
  async submit() {
    const err = this.validate();
    if (err) {
      this.error = err;
      m.redraw();
      return;
    }
    this.error = '';
    this.saving = true;
    m.redraw();

    // v0.6.7 — 拼 start_at / end_at: YYYY-MM-DD HH:MM:SS
    const startISO = `${this.form.date}T${this.form.startTime}:00`;
    const endISO = `${this.form.date}T${this.form.endTime}:00`;

    // v0.6.7 — payload (create + edit 共用)
    //   含 student_card_id (后端校验 belongs to student/school)
    //   + lesson_type_id / lesson_category_id (后端接受, 校验 single value, 不持久化 — schema 不允许)
    const payload = {
      school_id: +this.activeSchoolId,
      room_id: +this.form.room.id,
      // v0.6.7 — coach list 返 id (ListUsersByRoleController), staff list 返 user_id (ListSchoolStaffController)
      //   兼容两种 field 名
      coach_id: +(this.form.coach.id || this.form.coach.user_id),
      // student list 返 user_id (ListStudentsController) / id (没 schools-list 直接 user)
      student_id: +(this.form.student.user_id || this.form.student.id),
      student_card_id: +this.form.studentCard.id,
      // v0.6.7 — 课程类型/分类 (single-select) — 后端接受并校验 single value
      //   lesson model schema 不含这两个字段 (不持久化), 由 student_card.template 关联查
      lesson_type_id: this.form.lessonType,
      lesson_category_id: this.form.lessonCategory,
      // v0.12.0 — 计费课时 (半节课时)。只有 0.5 / 1 两个可能值 (chargeToggle 二选一 + 后端白名单)
      lesson_charge: this.form.lessonCharge,
      start_at: startISO,
      end_at: endISO,
      // v0.17.62 — 用户选的是哪个课表节点。**必须带**: 时间段允许重叠之后,
      //   只靠 start_at 后端分不出这节课该归哪一行 (10:45 同时落在两段里)。
      //   null = 用户没在下拉里选过 (老数据/自定义时间) ⇒ 后端回退到 start_at 反推。
      schedule_node_id: this.form.scheduleNodeId != null ? +this.form.scheduleNodeId : null,
      notes: this.form.notes || null,
      status: 'scheduled'
    };
    try {
      // v0.6.8 — mode 区分 create (POST) / edit (PATCH)
      if (this.mode === 'edit' && this.lesson && this.lesson.id) {
        await Api.update('lessons', this.lesson.id, payload);
      } else {
        await Api.create('lessons', payload);
      }
      this.saving = false;
      if (this.onCloseCb) this.onCloseCb();
      this.hide();
    } catch (e) {
      this.saving = false;
      // v0.17.65 — 为什么不能只看 `e.message` (改前这里只有 `|| '保存失败'`)
      //   Flarum 后端抛 ValidationException 时, HTTP 层是 422, **真实原因在 `e.response.errors[0].detail`**
      //   (如「教练时段冲突」「该课槽已有课, 请换时间段」); 而 `e.message` 是 mithril 请求层自己拼的
      //   "Response code 422 (Unprocessable Entity)" —— 与后端那句中文提示没有任何关系。
      //   后果: 正课保存失败时弹窗只显示「保存失败」, 前台拿不到「换教练 / 换教室 / 换时间段」
      //   这类可操作信息, 老师只能反复重试同一个必然失败的提交。
      //   形状照抄同仓既有正确写法: 本文件体验课路径 L1652 + CalendarTab.js:2770 + modal/FixedLessonModal.js:593。
      //   兜底文案「保存失败」保留不变, 只是从唯一一档降为最后一档 (网络层异常 / 非 422 时仍命中)。
      this.error = errReason(e, '保存失败');
      console.error('[booking-modal] save failed', e);
      m.redraw();
    }
  }

  // ─── View ──────────────────────────────────────────────────────

  // ─── v0.17.0 tab 栏 ────────────────────────────────────────────
  /**
   * 正课 / 体验课 切换栏。
   *
   * 复用 `.ZS-modeswitch` 既有样式 (forum.less L8010-8040, 课表顶部「正常约课 / 固定课」
   * 用的就是它), DOM 结构与 CalendarTab.js L3199-3223 同款 —— 不自造一套。
   *
   * ⚠️ edit mode 不渲染切换栏: 本轮「编辑」只支持正课。体验课的改期/取消走课表上的
   *   右键菜单, 不在这个 modal 里。硬塞一个「体验课」tab 进 edit 模式, 只会得到
   *   一份**空草稿** (edit 的回显数据全写在 drafts.normal 里), 看着能点、保存必炸。
   */
  tabBar() {
    if (this.mode === 'edit') return null;
    return m("div", {
      className: "ZS-modeswitch ZS-tf-tabs",
      role: "tablist",
      "aria-label": "\u7EA6\u8BFE\u7C7B\u578B"
    }, m("button", {
      type: "button",
      role: "tab",
      "aria-selected": this.tab === 'normal' ? 'true' : 'false',
      className: 'ZS-modeswitch-btn' + (this.tab === 'normal' ? ' is-on' : '')
      // ⚠️ 小写 onclick: Flarum 2 打包后大写 onClick → addEventListener('Click'),
      //   DOM 事件名大小写敏感 ⇒ 永不触发且不报错。全仓库既有写法都是小写。
      ,
      onclick: () => this.onTabSwitch('normal')
    }, "\u6B63\u8BFE"), m("button", {
      type: "button",
      role: "tab",
      "aria-selected": this.tab === 'trial' ? 'true' : 'false',
      className: 'ZS-modeswitch-btn' + (this.tab === 'trial' ? ' is-on' : ''),
      onclick: () => this.onTabSwitch('trial')
    }, "\u4F53\u9A8C\u8BFE"));
  }

  /** 体验课 tab 的整块渲染 (含自己的页脚: 文案 + 未选卡时置灰) */
  renderTrialTab() {
    const noCard = !this.drafts.trial.template;
    return m("div", {
      className: "ZS-tf-root"
    }, m("div", {
      className: "Modal-body"
    }, this.tabBar(), this.renderTrialPane()), m("div", {
      className: "Modal-footer"
    }, m("button", {
      className: "Button",
      onclick: () => this.hide()
    }, m("i", {
      className: "fas fa-xmark"
    }), " \u53D6\u6D88"), m("button", {
      className: "Button Button--primary"
      // ⚠️ 未选体验卡时置灰 (规格: 「保存按钮置灰」)。disabled 而不是在 submit 里
      //   报错 —— 让约束在按下之前就可见, 而不是按了才被拒。
      ,
      disabled: this.saving || noCard,
      onclick: () => this.submitTrial()
    }, this.saving ? '保存中...' : '保存体验课')));
  }
  content() {
    // v0.17.0 — 体验课 tab 整块换渲染 (含自己的 banner / 分区 / 页脚)。
    //   放在最前面 early return: 正课那条路径下面的每一行 JSX **一个字都没动**,
    //   这是「正课 tab 零回归」最直接的保证 —— 不是靠 diff 复查, 是物理上没有机会改到。
    if (this.tab === 'trial') {
      return this.renderTrialTab();
    }
    // v0.17.57 — Row 2 是单 Dropdown, 结束时间由**卡的 minutes** 算出 (recalcEndFromCard),
    //   时间段只决定「几点开始」。两者不再有「不一致」这回事, 所以不需要 CUSTOM 提示 ——
    //   showCustomEndHint / nodeEndTime 仍保持删除状态 (本轮没动它们)。
    return (
      /* v0.17.8 — 补 .ZS-nf-root (对标体验课的 .ZS-tf-root): 窄屏只靠这个 class 把
       *   本 tab 的 Modal-body 收到 16px。**不能**写成 `.Modal.ZS-BookingModal .Modal-body`
       *   —— 那会把两个 tab 一起改掉 (体验课那条限高/边距口径是 v0.17.6 刚修的)。 */
      m("div", {
        className: "ZS-nf-root"
      }, m("div", {
        className: "Modal-body"
      }, this.scopeBanner(), this.tabBar(), m("div", {
        className: "ZS-modal-form ZS-followup-form ZS-normal-form"
      }, renderSection(1, '排课信息', '选教室 · 教练 · 时间段', [/* Row 1 · 教室 + 教练 (2 列 grid) */
      m("div", {
        className: "ZS-form-row ZS-form-row-2col"
      }, m("div", {
        className: "ZS-form-field ZS-nf-room"
      }, m("label", {
        className: "ZS-form-label"
      }, "\u6559\u5BA4", m("span", {
        style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
      }, "*")), this.roomDropdown()), m("div", {
        className: "ZS-form-field ZS-nf-coach"
      }, m("label", {
        className: "ZS-form-label"
      }, "\u6559\u7EC3", m("span", {
        style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
      }, "*")), this.normalPicker.render('normal_coach_id'))),
      /* Row 2 · 时间 (v0.7.1 辉哥 10/2 拍板 A 方案 — 时间段改单 Dropdown, SCD 节点列表)
        *   日期 chip (readonly) + 单 Dropdown (HH:MM-HH:MM 节点列表)
        *   v0.17.5 — 选中节点即「占这一个区块」, 结束时间不再被卡时长改写,
        *     所以下面不再有任何 CUSTOM hint。 */
      m("div", {
        className: "ZS-form-row full"
      }, m("div", {
        className: "ZS-form-field ZS-nf-time"
      }, m("label", {
        className: "ZS-form-label"
      }, "\u65F6\u95F4", m("span", {
        style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
      }, "*")), m("div", {
        className: "ZS-time-row"
      }, this.renderDateField('normal'), this.timeSlotDropdown())))]), renderSection(2, '学员与课程', '选学员及其持卡', [/* Row 3 · 学员 + 学员卡 (2 列 grid) */
      m("div", {
        className: "ZS-form-row ZS-form-row-2col"
      }, m("div", {
        className: "ZS-form-field ZS-nf-student"
      }, m("label", {
        className: "ZS-form-label"
      }, "\u5B66\u5458", m("span", {
        style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
      }, "*")), this.normalPicker.render('normal_student_id')), m("div", {
        className: "ZS-form-field ZS-nf-card"
      }, m("label", {
        className: "ZS-form-label"
      }, "\u5B66\u5458\u5361", m("span", {
        style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
      }, "*")), this.studentCardDropdown())), /* Row 4 · 课程类型 + 课程分类 (2 列 grid, filtered by 卡) */
      m("div", {
        className: "ZS-form-row ZS-form-row-2col"
      }, m("div", {
        className: "ZS-form-field ZS-nf-type"
      }, m("label", {
        className: "ZS-form-label"
      }, "\u8BFE\u7A0B\u7C7B\u578B", m("span", {
        style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
      }, "*")), this.lessonTypeDropdown()), m("div", {
        className: "ZS-form-field ZS-nf-cat"
      }, m("label", {
        className: "ZS-form-label"
      }, "\u8BFE\u7A0B\u5206\u7C7B", m("span", {
        style: "color:var(--zs-danger-e55050, #e55050);margin-left:2px;"
      }, "*")), this.lessonCategoryDropdown())),
      /* Row 4b · v0.12.0 计费课时 (半节课时, 辉哥 10/9 拍板)
        *   二选一 (半节 / 一节), 默认一节。不联动时长 —— 辉哥明确要求
        *   「不要把课块变为一半」, 半节只影响**扣多少课时**, 时长照旧。 */
      m("div", {
        className: "ZS-form-row full"
      }, m("div", {
        className: "ZS-form-field ZS-nf-charge"
      }, m("label", {
        className: "ZS-form-label"
      }, "\u8BA1\u8D39\u8BFE\u65F6"), this.chargeToggle(), this.mode === 'edit' && this.lesson && this.lesson.status === 'completed' && m("div", {
        className: "ZS-auto-end-hint"
      }, m("span", {
        className: "ZS-auto-tag"
      }, "[LOCKED]"), m("span", null, "\u5DF2\u6D88\u8BFE, \u8BA1\u8D39\u8BFE\u65F6\u4E0D\u53EF\u4FEE\u6539"))))]), renderSection(3, '备注', '可留空', [/* Row 5 · 备注 */
      m("div", {
        className: "ZS-form-row full"
      }, m("div", {
        className: "ZS-form-field ZS-nf-notes"
      }, m("label", {
        className: "ZS-form-label"
      }, "\u5907\u6CE8"), m("textarea", {
        className: "ZS-form-input ZS-form-textarea",
        placeholder: "\u5B66\u5458\u7279\u6B8A\u60C5\u51B5 / \u6559\u7EC3\u5907\u6CE8 / \u88C5\u5907\u9700\u6C42...",
        value: this.form.notes,
        oninput: e => {
          this.form.notes = e.target.value;
        }
      })))]), this.error && m("div", {
        className: "ZS-newversion-error",
        style: "margin-top:8px;"
      }, m("i", {
        className: "fas fa-triangle-exclamation",
        style: "color:var(--zs-danger-ef4444, #ef4444);margin-right:6px;"
      }), this.error), this.liveConflictNotice())), m("div", {
        className: "Modal-footer"
      }, m("button", {
        className: "Button",
        onclick: () => this.hide()
      }, "\u53D6\u6D88"), m("button", {
        className: "Button Button--primary",
        disabled: this.saving,
        onclick: () => this.submit()
      }, this.saving ? '保存中...' : this.mode === 'edit' ? '保存修改' : '保存约课')))
    );
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/LessonEditorModal', LessonEditorModal);